import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { API } from "./cache";
import "./ReportsView.css";
import "./MasterViewV2.css";
import "./AppSidebar.css";
import "./V2SideDrawer.css";
import FilterTriggerButton from "./features/common/components/FilterTriggerButton";
import ChoiceFilterPopover from "./features/common/components/ChoiceFilterPopover";
import TaskQuickViewDrawer from "./features/tasks/components/TaskQuickViewDrawer";
import DefectQuickViewDrawer from "./features/defects/components/DefectQuickViewDrawer";
import { listReportFavorites, addReportFavorite, removeReportFavorite } from "./features/common/services/reportFavoritesApi";
import { buildFilteredRows, countActiveFilters } from "./features/common/services/filterFieldUtils";

const EE_STATUS_OPTS = [
  { value: "Planning",           label: "Planning" },
  { value: "In-Progress",        label: "In-Progress" },
  { value: "Regression Testing", label: "Regression Testing" },
  { value: "UAT Released",       label: "UAT Released" },
  { value: "Staging Released",   label: "Staging Released" },
  { value: "Live",               label: "Live" },
  { value: "Closed",             label: "Closed" },
];

function escHtml(s) {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

// Epic Effort row-list pace indicator — same 3-way status/colors as the
// Dashboard Utilization tab (GET /dashboard/utilization's pace_status),
// just computed here off this report's own task-only actual_hours (see
// GET /reports/epic-effort's docstring for why the two reports'
// actual_hours deliberately differ, and CLAUDE.md's Utilization bullet for
// the buffer-based over/behind logic these colors represent).
const EE_PACE_COLORS = { over: "#B03A2E", behind: "#C8842E", on_pace: "#4a7c5c" };
const EE_PACE_LABELS = { over: "Over pace", behind: "Behind pace", on_pace: "On pace" };
// Same 3 keys/labels as EE_PACE_LABELS, as filter checkbox options — kept as
// a separate array (rather than deriving one from the other) since this one
// needs {value,label} shape for the filter-drawer's generic multi-select
// renderer (matches EE_STATUS_OPTS's own shape).
const EE_PACE_FILTER_OPTS = [
  { value: "on_pace", label: "On pace" },
  { value: "behind",  label: "Behind pace" },
  { value: "over",    label: "Over pace" },
];

// Default view excludes Closed epics — matches how most epics naturally
// leave the "active work" set. Reused by both the initial state and
// ChoiceFilterPopover's own Reset button, so "Reset" restores this default
// rather than clearing to a truly empty (all-statuses-included) filter set.
// Shape matches ChoiceFilterPopover's draft/appliedFilters convention:
// { [fieldKey]: { op: "is"|"is_not", values: [...] } } for choice fields,
// { type: "daterange", from, to } for date fields — sparse (a key is only
// present when actually set), same as Tasks/Defects/Projects/etc.
function eeDefaultFilters() {
  return {
    status_in: { op: "is", values: EE_STATUS_OPTS.filter(o => o.value !== "Closed").map(o => o.value) },
  };
}

function IconDownload() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function IconTable() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="9" x2="9" y2="21" />
    </svg>
  );
}

function IconBarChart() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="21" x2="21" y2="21" />
      <rect x="5" y="13" width="4" height="8" rx="1" />
      <rect x="10.5" y="8" width="4" height="13" rx="1" />
      <rect x="16" y="4" width="4" height="17" rx="1" />
    </svg>
  );
}

// ── Report chooser icons (24x24 stroke glyphs, matching the icons above) ──────
function IconSearch() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function IconStar({ filled }) {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}
function IconClock() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 16 14" />
    </svg>
  );
}
function IconCalendar() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
function IconAlertTriangle() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function IconClipboardCheck() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <polyline points="9 14 11 16 15 12" />
    </svg>
  );
}
function IconUsers() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconUserCheck() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" />
      <polyline points="17 11 19 13 23 9" />
    </svg>
  );
}
function IconPuzzle() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19.439 7.85c-.049.322.059.648.289.878l1.568 1.568c.47.47.706 1.087.706 1.704s-.235 1.233-.706 1.704l-1.611 1.611a.98.98 0 0 1-.837.276c-.47-.07-.802-.48-.968-.925a2.501 2.501 0 1 0-3.214 3.214c.446.166.855.497.925.968a.979.979 0 0 1-.276.837l-1.611 1.611a2.404 2.404 0 0 1-1.704.706 2.402 2.402 0 0 1-1.704-.706l-1.568-1.568a1.026 1.026 0 0 0-.877-.29c-.493.074-.84.504-1.02.968a2.5 2.5 0 1 1-3.237-3.237c.464-.18.894-.527.967-1.02a1.026 1.026 0 0 0-.289-.877l-1.568-1.568A2.402 2.402 0 0 1 1.998 12c0-.617.236-1.234.706-1.704L4.315 8.685a.979.979 0 0 1 .837-.276c.47.07.802.48.968.925a2.501 2.501 0 1 0 3.214-3.214c-.446-.166-.855-.497-.925-.968a.98.98 0 0 1 .276-.837l1.611-1.611A2.402 2.402 0 0 1 12 1.998c.617 0 1.234.236 1.704.706l1.568 1.568c.23.23.556.338.877.29.493-.074.84-.504 1.02-.968a2.5 2.5 0 1 1 3.237 3.237c-.464.18-.894.527-.967 1.02z" />
    </svg>
  );
}

const REPORT_CATALOG = [
  {
    key: "monthly-timesheet", title: "Monthly Timesheet Report",
    desc: "On-demand employee hours by day with team, holidays and leave-aware averages",
    category: "time", featured: true, Icon: IconCalendar, accent: "maroon",
  },
  {
    key: "nc", title: "Non-Compliance Report",
    desc: "Users with NC marked against them",
    category: "compliance", featured: true, Icon: IconAlertTriangle, accent: "amber",
  },
  {
    key: "timesheet", title: "Timesheet Entry",
    desc: "Detailed Timesheet entry of all users",
    category: "time", featured: false, Icon: IconClock, accent: "green",
  },
  {
    key: "compliance-records", title: "Compliance Records",
    desc: "User-wise daily compliance history for timesheet, tasks, allocation and defects",
    category: "compliance", featured: false, Icon: IconClipboardCheck, accent: "emerald",
  },
  {
    key: "efforts-customer", title: "Efforts by Customer",
    desc: "Time spent per customer during the reporting period for each team",
    category: "effort", featured: false, Icon: IconUsers, accent: "purple",
  },
  {
    key: "efforts-team", title: "Efforts per Team",
    desc: "Time spent per team based on Tasks, Defects, Meetings and Others",
    category: "effort", featured: false, Icon: IconUserCheck, accent: "emerald",
  },
  {
    key: "efforts-product", title: "Efforts Per Product",
    desc: "Time spent based on each product, feature and sub-feature",
    category: "effort", featured: false, Icon: IconPuzzle, accent: "cyan",
  },
  {
    key: "epic-effort", title: "Epic Effort Report",
    desc: "Planned vs actual effort per epic, broken down by team with percentage contribution",
    category: "effort", featured: false, Icon: IconBarChart, accent: "navy",
  },
];

// AM/Team-Lead callers (anyone who reaches /reports without being a full
// Admin/Super Admin) only ever see these two reports — every other report
// stays Admin-only. Enforced here (catalog visibility) AND at the backend
// (require_admin_am_or_team_lead on these two endpoints specifically, plain
// require_admin on the rest) — this list existing client-side is only a UX
// convenience, not the real access boundary.
const AM_TEAM_LEAD_REPORT_KEYS = new Set(["efforts-customer", "epic-effort"]);

function isFullAdmin(user) {
  const role = String(user?.db_role || "").trim().toLowerCase();
  return role === "admin" || role === "super admin";
}

const REPORT_CATEGORIES = [
  { key: "all",        label: "All" },
  { key: "time",       label: "Time & Logs",      dot: "green" },
  { key: "compliance", label: "Compliance",       dot: "amber" },
  { key: "effort",     label: "Effort & Velocity", dot: "purple" },
  { key: "favorite",   label: "Starred" },
];

const RV_ACCENT_COLORS = {
  maroon:  "#933d3d",
  amber:   "#ca8a04",
  green:   "#4a7c59",
  emerald: "#059669",
  purple:  "#6b21a8",
  cyan:    "#0891b2",
  navy:    "#334155",
};

function IconArrowRight() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function ReportChooserCard({ report, featured, isFavorite, onToggleFavorite, onOpen }) {
  const { title, desc, Icon, accent } = report;
  const color = RV_ACCENT_COLORS[accent] || RV_ACCENT_COLORS.navy;

  // Whole card is clickable (not just the arrow/CTA) — role="button" +
  // tabIndex + onKeyDown so it's keyboard-activatable too, since a plain
  // div onClick alone isn't. The favorite star has its own onClick with
  // stopPropagation (a distinct action from opening the report); the
  // arrow/CTA element deliberately has NO onClick of its own anymore —
  // clicking it still opens the report purely via the native click bubbling
  // up to this div's handler.
  const openOnKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); }
  };

  if (featured) {
    return (
      <div
        className="rv-report-card rv-report-card--featured"
        style={{ borderLeftColor: color }}
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={openOnKeyDown}
      >
        <div className="rv-report-card__head">
          <div className="rv-report-card__icon rv-report-card__icon--lg" style={{ background: `${color}1A`, color }}>
            <Icon />
          </div>
          <button
            type="button"
            className={`rv-report-card__fav${isFavorite ? " rv-report-card__fav--active" : ""}`}
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
            title={isFavorite ? "Remove from starred" : "Add to starred"}
          >
            <IconStar filled={isFavorite} />
          </button>
        </div>
        <h3 className="rv-report-card__title rv-report-card__title--lg">{title}</h3>
        <p className="rv-report-card__desc">{desc}</p>
        <div className="rv-report-card__cta">
          <span className="rv-report-card__cta-label">
            Run Report
            <IconArrowRight />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rv-report-card"
      style={{ borderLeftColor: color }}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={openOnKeyDown}
    >
      <div className="rv-report-card__icon" style={{ background: `${color}1A`, color }}>
        <Icon />
      </div>
      <div className="rv-report-card__body">
        <h3 className="rv-report-card__title">{title}</h3>
        <p className="rv-report-card__desc">{desc}</p>
      </div>
      <div className="rv-report-card__actions">
        <button
          type="button"
          className={`rv-report-card__fav${isFavorite ? " rv-report-card__fav--active" : ""}`}
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
          title={isFavorite ? "Remove from starred" : "Add to starred"}
        >
          <IconStar filled={isFavorite} />
        </button>
        <span className="rv-report-card__open" title="Open report">
          <IconArrowRight />
        </span>
      </div>
    </div>
  );
}

function ReportGroupSection({ group, favoriteKeys, onToggleFavorite, onOpen }) {
  return (
    <section className="rv-chooser__section">
      <div className="rv-chooser__section-head">
        <span className="rv-chooser__section-dot" style={{ background: RV_ACCENT_COLORS[group.dot] }} />
        <span>{group.label}</span>
        <span className="rv-chooser__section-count">{group.reports.length} report{group.reports.length === 1 ? "" : "s"}</span>
      </div>
      <div className="rv-chooser__grid">
        {group.reports.map((r) => (
          <ReportChooserCard
            key={r.key}
            report={r}
            isFavorite={favoriteKeys.has(r.key)}
            onToggleFavorite={() => onToggleFavorite(r.key)}
            onOpen={() => onOpen(r.key)}
          />
        ))}
      </div>
    </section>
  );
}

// Single-series horizontal bar chart (magnitude, one hue — no legend needed).
// Fits container width (bar length is a % of its own row's track, so every
// row shares the same scale); rows stack vertically and the panel scrolls
// vertically for long lists — never horizontally. Each row: bold label (+
// optional smaller muted sublabel, e.g. AM name), a bordered/outlined track
// with the fill flush against its left edge, and the value in bold ink to
// the right of the track (never inside it, so it's never clipped).
// `onBarClick`, when provided, is called with the FULL row object (not just
// its label) — callers must use row.id/row.user_id/etc. directly, never
// look a row back up by label. Two rows can legitimately share a display
// label (e.g. two distinct epics both named the same thing), and a
// find-by-label lookup would then always resolve to the same row
// regardless of which bar was actually clicked — this shipped once already
// (Efforts by Customer's Epic-level chart) and looked like a data bug
// (clicking a 102h bar drilled into an unrelated 58h epic) when it was
// actually this ambiguous-label lookup.
function EfcBarChart({ rows, onBarClick, emptyLabel, isRowExpanded, renderExpanded }) {
  if (!rows.length) {
    return <div className="rv-state efc-chart-empty">{emptyLabel}</div>;
  }
  const max = Math.max(...rows.map((r) => r.value), 1);
  const RowTag = onBarClick ? "button" : "div";
  return (
    <div className="efc-chart">
      {rows.map((r) => {
        const pct = r.value > 0 ? Math.max((r.value / max) * 100, 1.5) : 0;
        const expanded = !!(isRowExpanded && isRowExpanded(r));
        return (
          <Fragment key={r.id ?? r.user_id ?? r.key ?? r.label}>
            <RowTag
              type={onBarClick ? "button" : undefined}
              className={`efc-chart-row${onBarClick ? "" : " efc-chart-row--static"}${expanded ? " efc-chart-row--active" : ""}`}
              onClick={onBarClick ? () => onBarClick(r) : undefined}
              title={`${r.label}${r.sublabel ? ` (${r.sublabel})` : ""}: ${fmtTime(r.value)}`}
            >
              <span className="efc-chart-label">
                {r.label}
                {r.sublabel && <span className="efc-chart-sublabel">{r.sublabel}</span>}
              </span>
              <span className="efc-chart-track">
                <span className="efc-chart-bar" style={{ width: `${pct}%` }} />
              </span>
              <span className="efc-chart-value">{fmtTime(r.value)}</span>
            </RowTag>
            {expanded && renderExpanded && (
              <div className="efc-chart-expand">{renderExpanded(r)}</div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

// ── Single-ring SVG donut helpers (mirrors the annular-arc technique used by
// BreakdownDonut in DefectsViewV2Page.jsx, kept as an independent copy here
// per the one-view-one-implementation convention — no cross-view imports). ──
function efcPolarToXY(cx, cy, r, angleDeg) {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function efcAnnularArcPath(cx, cy, r, sw, startDeg, endDeg) {
  if (endDeg - startDeg < 0.3) return "";
  const p1 = efcPolarToXY(cx, cy, r, startDeg);
  const p2 = efcPolarToXY(cx, cy, r, endDeg);
  const p3 = efcPolarToXY(cx, cy, r - sw, endDeg);
  const p4 = efcPolarToXY(cx, cy, r - sw, startDeg);
  const large = (endDeg - startDeg) > 180 ? 1 : 0;
  return [
    `M ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`,
    `A ${r} ${r} 0 ${large} 1 ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`,
    `L ${p3.x.toFixed(2)} ${p3.y.toFixed(2)}`,
    `A ${r - sw} ${r - sw} 0 ${large} 0 ${p4.x.toFixed(2)} ${p4.y.toFixed(2)}`,
    "Z",
  ].join(" ");
}

// Fixed categorical order, assigned by rank (rows arrive pre-sorted by value
// desc from the backend) — cool-toned hues deliberately distinct from the
// warm accent orange (used for magnitude bars) and the warm severity reds
// used by the Defects donut, so identity slices never get confused with
// either of those two existing warm-toned conventions.
const EFC_DONUT_COLORS = ["#3D6C94", "#3E8E82", "#6E5B9E", "#4C7A3F", "#8C6B4F"];

// The two sidebar-donut dimensions (and their scope) for a given drill depth
// in the Efforts by Customer drill chain (Customer -> Project -> Epic ->
// Team -> Resource) — indexed by effortDrillPath.length (0..4). Each depth's
// pair is the two dimensions immediately below whatever's currently drilled
// into; Resource is the last org-level, so Team's pair and Resource's pair
// both reach past it into the Task/Defect content-level breakdown instead.
// Module-level (not a component-local function) since it's pure and doesn't
// close over any component state — keeps it a stable reference so the
// fetch effect that calls it never needs it in its dependency array.
const EFC_DONUT_PAIRS = [
  [{ dimension: "project",  title: "Top 5 Projects",  unit: "PROJECTS" },  { dimension: "epic",     title: "Top 5 Epics",     unit: "EPICS" }],
  [{ dimension: "epic",     title: "Top 5 Epics",     unit: "EPICS" },     { dimension: "team",     title: "Top 5 Teams",     unit: "TEAMS" }],
  [{ dimension: "team",     title: "Top 5 Teams",     unit: "TEAMS" },     { dimension: "resource", title: "Top 5 Resources", unit: "RESOURCES" }],
  [{ dimension: "resource", title: "Top 5 Resources", unit: "RESOURCES" }, { dimension: "task",     title: "Top 5 Tasks",     unit: "TASKS" }],
  [{ dimension: "task",     title: "Top 5 Tasks",     unit: "TASKS" },     { dimension: "defect",   title: "Top 5 Defects",   unit: "DEFECTS" }],
];

function efcDonutSpecsForPath(path) {
  const depth = path.length;
  const scopeParams = {};
  if (depth >= 1) scopeParams.customer_name = path[0].name;
  if (depth >= 2) scopeParams.project_id = path[1].id || "none";
  if (depth >= 3) scopeParams.epic_id = path[2].id || "none";
  if (depth >= 4) scopeParams.team_name = path[3].id || "none";
  return EFC_DONUT_PAIRS[depth].map((p) => ({ ...p, scopeParams }));
}

// Sidebar-donut pairing for the Effort per Team drill chain — NOT a reuse of
// EFC_DONUT_PAIRS above: that array assumes Customer's 5-deep chain with Team
// as an intermediate dimension, but here Team is the fixed anchor (passed as
// a scope param, never a "next dimension"), and the chain is 5 levels deep
// (Team -> Customer -> Project -> Epic -> Resource) with two different ways
// to reach the final Resource level (see eftFinalDepth in the component).
// Module-level and pure for the same reason efcDonutSpecsForPath is — no
// component state closed over, so the fetch effect never needs it in its
// dependency array.
function eftDonutSpecsForPath(mode, path) {
  const depth = path.length;
  if (depth === 0) {
    return [
      { dimension: "project",  title: "Top 5 Projects",  unit: "PROJECTS",  scopeParams: {} },
      { dimension: "resource", title: "Top 5 Resources", unit: "RESOURCES", scopeParams: {} },
    ];
  }
  const teamScope = { team_name: path[0].id || "none" };
  if (mode !== "full") {
    // Quick mode's only non-zero depth IS the final Resource level.
    return [
      { dimension: "task",   title: "Top 5 Tasks",   unit: "TASKS",   scopeParams: teamScope },
      { dimension: "defect", title: "Top 5 Defects", unit: "DEFECTS", scopeParams: teamScope },
    ];
  }
  if (depth === 1) { // Customer list within the chosen team
    return [
      { dimension: "project", title: "Top 5 Projects", unit: "PROJECTS", scopeParams: teamScope },
      { dimension: "epic",    title: "Top 5 Epics",    unit: "EPICS",    scopeParams: teamScope },
    ];
  }
  const customerScope = { ...teamScope, customer_name: path[1].name };
  if (depth === 2) { // Project list within team+customer
    return [
      { dimension: "epic",     title: "Top 5 Epics",     unit: "EPICS",     scopeParams: customerScope },
      { dimension: "resource", title: "Top 5 Resources", unit: "RESOURCES", scopeParams: customerScope },
    ];
  }
  const projectScope = { ...customerScope, project_id: path[2].id || "none" };
  if (depth === 3) { // Epic list within team+customer+project
    return [
      { dimension: "resource", title: "Top 5 Resources", unit: "RESOURCES", scopeParams: projectScope },
      { dimension: "task",     title: "Top 5 Tasks",     unit: "TASKS",     scopeParams: projectScope },
    ];
  }
  // depth === 4: Resource level (final), full mode — same content-level
  // pair as Efforts by Customer's own final level.
  const epicScope = { ...projectScope, epic_id: path[3].id || "none" };
  return [
    { dimension: "task",   title: "Top 5 Tasks",   unit: "TASKS",   scopeParams: epicScope },
    { dimension: "defect", title: "Top 5 Defects", unit: "DEFECTS", scopeParams: epicScope },
  ];
}

// Compact single-ring donut — Top-5-by-effort sidebar widget (Projects/Epics),
// shown alongside the main drill chart at every level of Efforts by Customer.
// Read-only (hover tooltip only, no click-to-drill — these span all
// customers so a click can't map onto the existing single-customer drill path).
function EfcMiniDonut({ title, unitLabel, rows, emptyLabel }) {
  const [hoverKey, setHoverKey] = useState(null);
  const [tip, setTip] = useState(null); // { x, y, arc }
  const cx = 100, cy = 100, r = 82, sw = 22;
  const GAP_DEG = 1.2;

  const total = rows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  const arcs = [];
  let offset = 0;
  rows.forEach((row, i) => {
    const value = Number(row.value) || 0;
    if (value <= 0 || total <= 0) return;
    const frac = value / total;
    const startDeg = offset * 360;
    const endDeg = (offset + frac) * 360 - GAP_DEG;
    arcs.push({ ...row, value, color: EFC_DONUT_COLORS[i % EFC_DONUT_COLORS.length], startDeg, endDeg });
    offset += frac;
  });

  const showTip = (e, arc) => {
    setHoverKey(arc.key);
    const wrap = e.currentTarget.closest(".efc-mini-donut-chart").getBoundingClientRect();
    setTip({ x: e.clientX - wrap.left + 12, y: e.clientY - wrap.top - 10, arc });
  };
  const hideTip = () => { setHoverKey(null); setTip(null); };

  return (
    <div className="efc-mini-donut">
      <div className="efc-mini-donut-head"><h4>{title}</h4></div>
      {arcs.length === 0 ? (
        <div className="rv-state efc-chart-empty">{emptyLabel}</div>
      ) : (
        <div className="efc-mini-donut-body">
          <div className="efc-mini-donut-chart">
            <svg className="efc-mini-donut-svg" viewBox="0 0 200 200">
              {arcs.map((arc, i) => {
                const path = efcAnnularArcPath(cx, cy, r, sw, arc.startDeg, arc.endDeg);
                const dim = hoverKey !== null && hoverKey !== arc.key;
                return (
                  <path
                    key={arc.key || i}
                    d={path}
                    fill={arc.color}
                    opacity={dim ? 0.3 : 0.92}
                    style={{ transition: "opacity 0.15s" }}
                    onMouseEnter={(e) => showTip(e, arc)}
                    onMouseMove={(e) => showTip(e, arc)}
                    onMouseLeave={hideTip}
                  />
                );
              })}
              <text x={cx} y={cy - 4} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontWeight={700} fontSize={20} fill="#0B0E13" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtTime(total)}</text>
              <text x={cx} y={cy + 14} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize={7.5} fill="#5C6069" letterSpacing="0.18em">{unitLabel}</text>
            </svg>
            {tip && (
              <div className="efc-mini-donut-tip" style={{ left: tip.x, top: tip.y }}>
                <div className="ttl">{tip.arc.label}</div>
                {tip.arc.sublabel && <div className="sub">{tip.arc.sublabel}</div>}
                <div className="row">
                  <span className="v">{fmtTime(tip.arc.value)}</span>
                  <span className="p">{total > 0 ? ((tip.arc.value / total) * 100).toFixed(1) : "0.0"}%</span>
                </div>
              </div>
            )}
          </div>
          <ul className="efc-mini-donut-legend">
            {arcs.map((arc, i) => {
              const pct = total > 0 ? ((arc.value / total) * 100).toFixed(0) : 0;
              return (
                <li
                  key={arc.key || i}
                  className={hoverKey === arc.key ? "on" : ""}
                  onMouseEnter={() => setHoverKey(arc.key)}
                  onMouseLeave={() => setHoverKey(null)}
                >
                  <span className="sw" style={{ background: arc.color }} />
                  <span className="lbl" title={arc.sublabel ? `${arc.label} (${arc.sublabel})` : arc.label}>{arc.label}</span>
                  <span className="val">{fmtTime(arc.value)}</span>
                  <span className="pct">{pct}%</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function toLocalISO(d) {
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function prevDay() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toLocalISO(d);
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function startOfWeekMonday(d) {
  const day = d.getDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day; // days to subtract to reach Monday
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  return monday;
}

// Epic Effort "Period" preset definitions — each computes { from, to } as
// local-date ISO strings (matching toLocalISO), written into the report's
// existing start_from/start_to (Epic Start Date) fields.
const EE_PERIOD_PRESETS = [
  {
    key: "week_td", label: "Week Till Date",
    range: () => { const now = new Date(); return { from: toLocalISO(startOfWeekMonday(now)), to: toLocalISO(now) }; },
  },
  {
    key: "month_td", label: "Month Till Date",
    range: () => { const now = new Date(); return { from: toLocalISO(startOfMonth(now)), to: toLocalISO(now) }; },
  },
  {
    key: "last_month", label: "Last Month",
    range: () => { const now = new Date(); const lm = addMonths(now, -1); return { from: toLocalISO(startOfMonth(lm)), to: toLocalISO(endOfMonth(lm)) }; },
  },
  {
    key: "last_3_months", label: "Last 3 Months",
    range: () => { const now = new Date(); return { from: toLocalISO(startOfMonth(addMonths(now, -3))), to: toLocalISO(endOfMonth(addMonths(now, -1))) }; },
  },
  {
    key: "last_6_months", label: "Last 6 Months",
    range: () => { const now = new Date(); return { from: toLocalISO(startOfMonth(addMonths(now, -6))), to: toLocalISO(endOfMonth(addMonths(now, -1))) }; },
  },
];

function fmtTime(totalMins) {
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h === 0 && m === 0) return "0h";
  if (m === 0) return `${h}h`;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

const EFD_CATEGORY_COLUMNS = [
  { key: "task_minutes",    label: "Task hours",    entryCategory: "Tasks" },
  { key: "defect_minutes",  label: "Defect hours",  entryCategory: "Defects" },
  { key: "meeting_minutes", label: "Meeting hours", entryCategory: "Meetings" },
  { key: "other_minutes",   label: "Other hours",   entryCategory: "Others" },
];

function fmtHoursDecimal(hours) {
  const val = Number(hours) || 0;
  return Number.isInteger(val) ? `${val}h` : `${val.toFixed(1)}h`;
}

function isWeekdayIso(isoDate) {
  if (!isoDate) return false;
  const d = new Date(`${String(isoDate).slice(0, 10)}T00:00:00`);
  const day = d.getDay();
  return day >= 1 && day <= 5;
}

function filterWeekdayEntries(entries) {
  const list = Array.isArray(entries) ? entries : [];
  return list.filter((r) => isWeekdayIso(r?.report_date));
}

function sumMinutesByUser(entries) {
  const map = new Map();
  (Array.isArray(entries) ? entries : []).forEach((r) => {
    if (!isWeekdayIso(r?.report_date)) return;
    const uid = r?.user_id || r?.user_name;
    if (!uid) return;
    const mins = (Number(r?.hours_spent) || 0) * 60 + (Number(r?.minutes_spent) || 0);
    map.set(uid, (map.get(uid) || 0) + mins);
  });
  return map;
}

function aggregateByUser(entries) {
  const byUser = new Map();
  (Array.isArray(entries) ? entries : []).forEach((r) => {
    const uid = r?.user_id || r?.user_name;
    if (!uid) return;
    if (!byUser.has(uid)) {
      byUser.set(uid, {
        user_id: uid,
        display_name: r?.user_display_name || r?.user_name || "—",
        taskIds: new Set(),
        defectIds: new Set(),
        totalMins: 0,
      });
    }
    const row = byUser.get(uid);
    row.totalMins += (Number(r?.hours_spent) || 0) * 60 + (Number(r?.minutes_spent) || 0);
    if (r?.ref_type === "task" && r?.ref_id) row.taskIds.add(r.ref_id);
    if (r?.ref_type === "defect" && r?.ref_id) row.defectIds.add(r.ref_id);
  });
  return Array.from(byUser.values()).sort((a, b) => String(a.display_name).localeCompare(String(b.display_name)));
}

function filterWeekdayMeetingUsers(users) {
  const list = Array.isArray(users) ? users : [];
  return list.map((u) => {
    const entries = (Array.isArray(u?.entries) ? u.entries : []).filter((e) => isWeekdayIso(e?.meeting_date));
    const totalMins = entries.reduce((s, e) => s + (Number(e?.total_minutes) || 0), 0);
    const meetingCount = entries.length;
    return {
      ...u,
      entries,
      total_minutes: totalMins,
      meeting_count: meetingCount,
    };
  });
}

function mergeRowsWithMeetings(rows, meetingUsers) {
  const out = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    out.set(r.user_id, {
      ...r,
      meetingCount: Number(r?.meetingCount) || 0,
      meetingMins: Number(r?.meetingMins) || 0,
      meetingEntries: Array.isArray(r?.meetingEntries) ? r.meetingEntries : [],
    });
  });

  (Array.isArray(meetingUsers) ? meetingUsers : []).forEach((u) => {
    const uid = u?.user_id || u?.user_name;
    if (!uid) return;
    const curr = out.get(uid);
    const patch = {
      meetingCount: Number(u?.meeting_count ?? u?.meetingCount) || 0,
      meetingMins: Number(u?.total_minutes ?? u?.meeting_minutes ?? u?.meetingMins) || 0,
      meetingEntries: Array.isArray(u?.entries) ? u.entries : [],
    };

    if (!curr) {
      out.set(uid, {
        user_id: uid,
        display_name: u?.display_name || u?.user_display_name || u?.user_name || "—",
        taskIds: new Set(),
        defectIds: new Set(),
        totalMins: 0,
        ...patch,
      });
      return;
    }

    out.set(uid, { ...curr, ...patch });
  });

  return Array.from(out.values()).sort((a, b) => String(a.display_name).localeCompare(String(b.display_name)));
}

function countWorkingDays(from, to) {
  if (!from || !to) return 1;
  const s = new Date(`${from}T00:00:00`);
  const e = new Date(`${to}T00:00:00`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 1;
  if (s > e) return 1;
  let count = 0;
  const cur = new Date(s);
  while (cur <= e) {
    const d = cur.getDay();
    if (d >= 1 && d <= 5) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return Math.max(1, count);
}

function fmtAvg(totalMinutes, workingDays) {
  const mins = Number(totalMinutes) || 0;
  const wd = Math.max(1, Number(workingDays) || 1);
  const avgHours = mins / 60 / wd;
  return `${avgHours.toFixed(1)}h`;
}

const NC_FILTER_OPS = ["=", ">", ">=", "<", "<="];

function matchesNumericFilter(value, filter) {
  const raw = String(filter?.value || "").trim();
  if (!raw) return true;
  const target = Number(raw);
  if (Number.isNaN(target)) return true;
  const actual = Number(value) || 0;

  switch (filter?.op) {
    case "=":
      return actual === target;
    case ">":
      return actual > target;
    case ">=":
      return actual >= target;
    case "<":
      return actual < target;
    case "<=":
      return actual <= target;
    default:
      return true;
  }
}

function fmtDisplayDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

const EXCEL_DURATION_FMT = "[h]:mm";
function minsToExcelDuration(totalMins) {
  const mins = Number(totalMins) || 0;
  return mins / 1440; // Excel stores time as fraction of a day
}

// ── Ref Entries Popup ───────────────────────────────────────────────────────────
function RefEntriesPopup({ title, entries, onClose }) {
  return (
    <div className="rv-popup-overlay" onClick={onClose}>
      <div className="rv-popup" onClick={e => e.stopPropagation()}>
        <div className="rv-popup-header">
          <span className="rv-popup-title">{title}</span>
          <button className="rv-popup-close" onClick={onClose} type="button">✕</button>
        </div>
        <div className="rv-popup-body">
          {entries.length === 0 ? (
            <div className="rv-popup-empty">No entries found.</div>
          ) : (
            <table className="rv-popup-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Entry Title</th>
                  <th>Customer</th>
                  <th>Project</th>
                  <th>Time</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={e.id || i}>
                    <td className="rv-popup-td--date">{e.report_date ? fmtDisplayDate(e.report_date.slice(0, 10)) : "—"}</td>
                    <td>{e.title || "—"}</td>
                    <td className="rv-popup-td--meta">{e.resolved_customer_name || "—"}</td>
                    <td className="rv-popup-td--meta">{e.resolved_project_name || "—"}</td>
                    <td className="rv-popup-td--time">
                      {(e.hours_spent || 0) > 0 || (e.minutes_spent || 0) > 0
                        ? `${e.hours_spent || 0}h ${e.minutes_spent || 0}m`
                        : "—"}
                    </td>
                    <td>
                      <span className={`rv-popup-badge rv-popup-badge--${(e.status || "draft").toLowerCase().replace(/\s/g, "-")}`}>
                        {e.status || "Draft"}
                      </span>
                    </td>
                    <td className="rv-popup-td--notes">{e.overall_notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function MeetingEntriesPopup({ title, entries, onClose }) {
  return (
    <div className="rv-popup-overlay" onClick={onClose}>
      <div className="rv-popup" onClick={e => e.stopPropagation()}>
        <div className="rv-popup-header">
          <span className="rv-popup-title">{title}</span>
          <button className="rv-popup-close" onClick={onClose} type="button">✕</button>
        </div>
        <div className="rv-popup-body">
          {entries.length === 0 ? (
            <div className="rv-popup-empty">No meetings found.</div>
          ) : (
            <table className="rv-popup-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Meeting</th>
                  <th>Source</th>
                  <th>Organizer</th>
                  <th>Time Spent</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={e.meeting_key || e.ref_id || i}>
                    <td className="rv-popup-td--date">{e.meeting_date ? fmtDisplayDate(String(e.meeting_date).slice(0, 10)) : "—"}</td>
                    <td>{e.title || "—"}</td>
                    <td className="rv-popup-td--meta">{e.ref_type === "teams_meeting" ? "Teams" : "Meeting"}</td>
                    <td className="rv-popup-td--meta">{e.organizer_name || "—"}</td>
                    <td className="rv-popup-td--time">{fmtTime(Number(e.total_minutes) || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportsDateFilterPopover({ open, dateFrom, dateTo, showExcluded, onApply, onClose }) {
  const [draftFrom,        setDraftFrom]        = useState(dateFrom     || "");
  const [draftTo,          setDraftTo]          = useState(dateTo       || "");
  const [draftShowExcluded, setDraftShowExcluded] = useState(showExcluded ?? false);

  useEffect(() => {
    if (!open) return;
    setDraftFrom(dateFrom || "");
    setDraftTo(dateTo || "");
    setDraftShowExcluded(showExcluded ?? false);
  }, [open, dateFrom, dateTo, showExcluded]);

  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="mv2-fdr-scrim" onClick={onClose} />
      <div className="mv2-fdr" style={{
        "--mv2-surface":       "#FFFFFF",
        "--mv2-surface-2":     "#EDE8DC",
        "--mv2-surface-hover": "#E3DCCB",
        "--mv2-border":        "#D7D0BE",
        "--mv2-border-strong": "#C8C0AA",
        "--mv2-text":          "#0B0E13",
        "--mv2-text-muted":    "#5C6069",
        "--mv2-text-faint":    "#8B8F99",
        "--mv2-accent":        "#C8842E",
        "--mv2-accent-bg":     "#F4E7CC",
        "--mv2-danger":        "#B03A2E",
        "--mv2-danger-bg":     "#F3DCD4",
        "--mv2-mono":          "'JetBrains Mono', ui-monospace, Menlo, monospace",
        "--mv2-radius-sm":     "4px",
        "--mv2-radius":        "8px",
      }}>
        <div className="mv2-fdr-header">
          <span className="mv2-fdr-title">Filter</span>
          <button className="mv2-fdr-reset" onClick={() => { setDraftFrom(""); setDraftTo(""); setDraftShowExcluded(false); }}>Reset</button>
        </div>
        <div className="mv2-fdr-list">
          <div className="mv2-fdr-field mv2-fdr-field--open">
            <div className="mv2-fdr-field-hdr" style={{ cursor: "default" }}>
              <span className="mv2-fdr-field-name">
                Date Range
                {(draftFrom || draftTo) && <span className="mv2-fdr-badge">✓</span>}
              </span>
            </div>
            <div className="mv2-fdr-field-body">
              <div className="mv2-fdr-daterange">
                <div className="mv2-fdr-daterange-row">
                  <label className="mv2-fdr-daterange-label">From</label>
                  <input
                    type="date"
                    className="mv2-fdr-date-input"
                    value={draftFrom}
                    max={draftTo || undefined}
                    onChange={e => setDraftFrom(e.target.value)}
                  />
                </div>
                <div className="mv2-fdr-daterange-row">
                  <label className="mv2-fdr-daterange-label">To</label>
                  <input
                    type="date"
                    className="mv2-fdr-date-input"
                    value={draftTo}
                    min={draftFrom || undefined}
                    onChange={e => setDraftTo(e.target.value)}
                  />
                </div>
                {(draftFrom || draftTo) && (
                  <button
                    className="mv2-fdr-daterange-clear"
                    onClick={() => { setDraftFrom(""); setDraftTo(""); }}
                  >
                    Clear dates
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="mv2-fdr-field mv2-fdr-field--open">
            <div className="mv2-fdr-field-hdr" style={{ cursor: "default" }}>
              <span className="mv2-fdr-field-name">
                Excluded Users
                {draftShowExcluded && <span className="mv2-fdr-badge">✓</span>}
              </span>
            </div>
            <div className="mv2-fdr-field-body">
              <div className="rv-excl-toggle">
                <button
                  type="button"
                  className={`rv-excl-toggle-btn${!draftShowExcluded ? " rv-excl-toggle-btn--active" : ""}`}
                  onClick={() => setDraftShowExcluded(false)}
                >
                  Hide
                </button>
                <button
                  type="button"
                  className={`rv-excl-toggle-btn${draftShowExcluded ? " rv-excl-toggle-btn--active" : ""}`}
                  onClick={() => setDraftShowExcluded(true)}
                >
                  Show
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="mv2-fdr-footer">
          <button className="mv2-fdr-find" onClick={() => { onApply(draftFrom, draftTo, draftShowExcluded); onClose(); }}>Find</button>
          <button className="mv2-fdr-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

function monthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function today() {
  return toLocalISO(new Date());
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtMonthLabel(month) {
  if (!month) return "";
  const d = new Date(`${month}-01T00:00:00`);
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

const CR_TYPES = ["timesheet", "task", "allocation", "defect"];
const CR_TYPE_LABELS = { timesheet: "Timesheet", task: "Tasks", allocation: "Allocation", defect: "Defects" };

function crRateClass(rate, type) {
  if (type === "task" || type === "defect") {
    if (rate >= 90) return "cr-cell--green";
    if (rate >= 60) return "cr-cell--amber";
    return "cr-cell--red";
  }
  if (rate >= 90) return "cr-cell--green";
  if (rate >= 70) return "cr-cell--amber";
  return "cr-cell--red";
}

function fmtRate(rate) {
  return `${rate.toFixed(1)}%`;
}

function fmtDisplayDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export default function ReportsView({ showToast, user }) {
  const [selectedReport, setSelectedReport] = useState(null);
  const reportCatalogForUser = useMemo(
    () => (isFullAdmin(user) ? REPORT_CATALOG : REPORT_CATALOG.filter((r) => AM_TEAM_LEAD_REPORT_KEYS.has(r.key))),
    [user]
  );

  // ── Report chooser: search / category filter / favorites ──────────────────
  const [chooserSearch,   setChooserSearch]   = useState("");
  const [chooserCategory, setChooserCategory] = useState("all");
  const [favoriteKeys,    setFavoriteKeys]    = useState(() => new Set());
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listReportFavorites()
      .then((keys) => { if (!cancelled) setFavoriteKeys(new Set(Array.isArray(keys) ? keys : [])); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setFavoritesLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const toggleReportFavorite = useCallback((key) => {
    const isFav = favoriteKeys.has(key);
    // Optimistic toggle — revert on failure rather than waiting on the
    // round-trip, since this is a low-stakes preference toggle.
    setFavoriteKeys((prev) => {
      const next = new Set(prev);
      isFav ? next.delete(key) : next.add(key);
      return next;
    });
    const request = isFav ? removeReportFavorite(key) : addReportFavorite(key);
    request.catch((err) => {
      setFavoriteKeys((prev) => {
        const next = new Set(prev);
        isFav ? next.add(key) : next.delete(key);
        return next;
      });
      showToast?.(err.message || "Failed to update favorite", "error");
    });
  }, [favoriteKeys, showToast]);

  // Keyboard shortcut: "/" or Cmd/Ctrl+K focuses the chooser search box,
  // only while on the chooser screen itself (not inside an open report).
  useEffect(() => {
    if (selectedReport) return;
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key === "/" || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k")) {
        e.preventDefault();
        document.getElementById("rv-chooser-search")?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedReport]);

  const chooserQuery = chooserSearch.trim().toLowerCase();
  const visibleReports = reportCatalogForUser.filter((r) => {
    if (chooserQuery && !r.title.toLowerCase().includes(chooserQuery) && !r.desc.toLowerCase().includes(chooserQuery)) return false;
    if (chooserCategory === "favorite") return favoriteKeys.has(r.key);
    if (chooserCategory !== "all") return r.category === chooserCategory;
    return true;
  });
  const showFeatured = chooserCategory === "all" && !chooserQuery;
  const featuredReports = showFeatured ? visibleReports.filter((r) => r.featured) : [];
  // Pinned "Favorites" banner at the very top of the default "All" view (not
  // shown while searching or a specific category/Starred pill is active —
  // the Starred pill already covers that case on its own). Reports can
  // legitimately appear here AND again in their normal spotlight/category
  // section below — this is a quick-access shortcut, not a move.
  const showFavoritesBanner = chooserCategory === "all" && !chooserQuery && favoriteKeys.size > 0;
  const favoriteBannerReports = showFavoritesBanner ? reportCatalogForUser.filter((r) => favoriteKeys.has(r.key)) : [];
  const groupedReports = REPORT_CATEGORIES.filter((c) => c.key !== "all" && c.key !== "favorite").map((c) => ({
    ...c,
    reports: visibleReports.filter((r) => r.category === c.key && !(showFeatured && r.featured)),
  })).filter((g) => g.reports.length > 0 || chooserCategory === g.key);
  // Display order: Favorites -> Effort & Velocity -> Operational Core
  // Spotlight (featured) -> remaining category groups. Effort & Velocity is
  // pulled out and rendered ahead of the featured section on purpose (not
  // just reordered within groupedReports's own array), since the featured
  // section always renders between it and the rest.
  const effortGroup  = groupedReports.find((g) => g.key === "effort");
  const otherGroups  = groupedReports.filter((g) => g.key !== "effort");
  const [dateFrom,  setDateFrom]  = useState(prevDay);
  const [dateTo,    setDateTo]    = useState(prevDay);
  const [tsFilterOpen, setTsFilterOpen] = useState(false);
  const [ncFilterOpen, setNcFilterOpen] = useState(false);
  const [ncFilters, setNcFilters] = useState({
    avg_minutes: { op: ">=", value: "" },
    overdue_tasks: { op: ">", value: "" },
    allocation_hours: { op: "<", value: "" },
  });
  const [ncRows,        setNcRows]        = useState([]);
  // Efforts by Customer: Customer -> Project -> Epic -> Team -> Resource drill
  // chain. effortDrillPath holds the selections made so far — its length is
  // the current depth: 0 = showing Customers, 1 = Projects (for path[0]'s
  // customer), 2 = Epics, 3 = Teams, 4 = Resources (handled by the separate
  // effortDrilldown* state below, same shape/endpoints as before, just
  // re-scoped to project/epic/team instead of team/category).
  const [effortDrillPath, setEffortDrillPath] = useState([]); // [{level, id, name}]
  // Table vs. bar-chart display mode — shared across the Customer/Project/Epic/Team levels.
  const [effortLevelDisplayMode, setEffortLevelDisplayMode] = useState("chart");
  // Own date range for Efforts by Customer only — defaults to month-to-date,
  // independent of the shared dateFrom/dateTo used by Non-Compliance / Efforts
  // per Team / Efforts per Product (which default to yesterday-only).
  const [efcDateFrom, setEfcDateFrom] = useState(monthStart);
  const [efcDateTo,   setEfcDateTo]   = useState(today);
  // Contextual Top-5 sidebar donuts — the two dimensions shown (and their
  // scope) change with drill depth: Customer view -> Project+Epic (global),
  // Project view -> Epic+Team (scoped to that customer), Epic view ->
  // Team+Resource (scoped to that customer+project), Team view ->
  // Resource+Task (scoped to that customer+project+epic), Resource view ->
  // Task+Defect (scoped to that customer+project+epic+team). See
  // efcDonutSpecsForPath below for the exact pairing/scope per depth.
  const [efcDonutA, setEfcDonutA] = useState({ title: "", unit: "", rows: [], loading: true });
  const [efcDonutB, setEfcDonutB] = useState({ title: "", unit: "", rows: [], loading: true });
  // Normalized rows for whichever of the 4 non-resource levels is currently
  // showing: [{ key, id, label, sublabel?, total_minutes }].
  const [effortLevelData,    setEffortLevelData]    = useState({ rows: [], grand_total_minutes: 0 });
  // True for AM/Team-Lead callers — same "your allocated projects" scoping
  // convention as Epic Effort Report/Dashboard Utilization. Set from
  // whichever drill-level fetch last returned a response; identical for
  // every level within one session, so it doesn't matter which sets it.
  const [efcScoped,          setEfcScoped]          = useState(false);
  const [effortLevelLoading, setEffortLevelLoading] = useState(true);
  const [effortLevelSortKey, setEffortLevelSortKey] = useState(null); // null | "label" | "total"
  const [effortLevelSortDir, setEffortLevelSortDir] = useState("desc");
  // Resource level (effortDrillPath.length === 4) — per-user summary +
  // expandable date-wise entries. Same shape as before, just re-scoped params.
  const [effortDrilldownLoading,  setEffortDrilldownLoading]  = useState(false);
  const [effortDrilldownData,     setEffortDrilldownData]     = useState({ rows: [], totals: {} });
  const [effortDrilldownExpanded, setEffortDrilldownExpanded] = useState({}); // key -> { loading, rows }
  // Table vs. bar-chart display mode for the per-user drilldown (Resource level).
  const [effortDrilldownDisplayMode, setEffortDrilldownDisplayMode] = useState("chart");
  const [quickView, setQuickView] = useState(null); // { type: "task"|"defect", id }

  const openQuickView = useCallback((refType, refId) => {
    if (!refId) return;
    if (refType === "task") setQuickView({ type: "task", id: refId });
    else if (refType === "defect") setQuickView({ type: "defect", id: refId });
  }, []);
  const closeQuickView = useCallback(() => setQuickView(null), []);

  const renderEfdReference = (r) => {
    const code = r.task_code || r.defect_code;
    return (
      <>
        {code && (
          <button
            type="button"
            className="efd-ref-code"
            onClick={() => openQuickView(r.ref_type, r.ref_id)}
          >
            {code}
          </button>
        )}
        {code ? " " : ""}{r.title || "—"}
      </>
    );
  };
  const EFD_REF_TYPE_LABEL = { task: "Task", defect: "Defect", meeting: "Meeting", teams_meeting: "Teams" };
  const renderEfdType = (r) => EFD_REF_TYPE_LABEL[r.ref_type] || r.ref_type || "—";
  const [effortProductData, setEffortProductData] = useState({ categories: [], rows: [], category_totals: {}, grand_total_minutes: 0 });

  // Effort per Team: Team -> Resource (quick mode) or Team -> Customer ->
  // Project -> Epic -> Resource (full mode) drill chain — mirrors the
  // Efforts by Customer drill chain above, anchored on Team instead of
  // Customer (with Customer itself folded in as depth 1 of the full drill).
  // All state here is prefixed eft* to stay fully independent of the
  // effort*/efc* Customer-report state (a user can have either report open).
  const [eftMode, setEftMode] = useState("simple"); // "simple" | "full"
  const [eftDateFrom, setEftDateFrom] = useState(monthStart);
  const [eftDateTo,   setEftDateTo]   = useState(today);
  const [eftDrillPath, setEftDrillPath] = useState([]); // [{level, id, name}]
  const [eftLevelDisplayMode, setEftLevelDisplayMode] = useState("chart");
  const [eftLevelData,    setEftLevelData]    = useState({ rows: [], grand_total_minutes: 0 });
  const [eftLevelLoading, setEftLevelLoading] = useState(true);
  const [eftLevelSortKey, setEftLevelSortKey] = useState(null);
  const [eftLevelSortDir, setEftLevelSortDir] = useState("desc");
  const [eftDonutA, setEftDonutA] = useState({ title: "", unit: "", rows: [], loading: true });
  const [eftDonutB, setEftDonutB] = useState({ title: "", unit: "", rows: [], loading: true });
  const [eftEntriesLoading,     setEftEntriesLoading]     = useState(false);
  const [eftEntriesData,        setEftEntriesData]        = useState({ rows: [], totals: {} });
  const [eftEntriesExpanded,    setEftEntriesExpanded]    = useState({});
  const [eftEntriesDisplayMode, setEftEntriesDisplayMode] = useState("chart");

  // Final drill depth differs by mode: quick mode ends at Team->Resource
  // (path length 1), full mode goes Team->Customer->Project->Epic->Resource
  // (path length 4). Switching modes mid-drill has no valid interpretation,
  // so it always resets the path back to the Team list.
  const eftFinalDepth = eftMode === "full" ? 4 : 1;

  const setEftModeAndReset = useCallback((mode) => {
    setEftMode(mode);
    setEftDrillPath([]);
  }, []);

  const eftDrillInto = useCallback((level, id, name) => {
    setEftDrillPath((prev) => [...prev, { level, id, name }]);
  }, []);

  const eftDrillToDepth = useCallback((depth) => {
    setEftDrillPath((prev) => prev.slice(0, depth));
  }, []);

  const EFT_LEVEL_NAMES = ["team", "customer", "project", "epic"];
  const [monthlyMonth, setMonthlyMonth] = useState(currentMonth);
  const [monthlyData, setMonthlyData] = useState({ date_columns: [], rows: [], summary: {} });
  const [tsReportData,  setTsReportData]  = useState(null);
  const [showExcluded,  setShowExcluded]  = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [expanded,       setExpanded]       = useState(true);
  const [showDetailView, setShowDetailView] = useState(false);
  const [refPopup,       setRefPopup]       = useState(null); // { title, entries }
  const [meetingPopup,   setMeetingPopup]   = useState(null); // { title, entries }

  // Epic Effort Report state
  const [eeRows,         setEeRows]         = useState([]);
  const [eeTeams,        setEeTeams]        = useState([]);
  const [eeLoading,      setEeLoading]      = useState(false);
  // True for AM/Team-Lead callers — the API scopes epics down to the
  // projects the caller has access to (project_user_mapping/
  // customer_user_mapping), same convention as the Dashboard Utilization
  // tab. Admin/Super Admin get the unscoped org-wide view and this is false.
  const [eeScoped,       setEeScoped]       = useState(false);
  const [eeFilterOpen,   setEeFilterOpen]   = useState(false);
  const [eeFilters,      setEeFilters]      = useState(eeDefaultFilters);
  const [eeFilterMode,   setEeFilterMode]   = useState("all");
  const [eeCustomerOpts,   setEeCustomerOpts]   = useState([]);
  const [eeAmOpts,         setEeAmOpts]         = useState([]);
  const [eeEpicOpts,       setEeEpicOpts]       = useState([]);

  // Compliance Records state
  const [crDateFrom,    setCrDateFrom]    = useState(monthStart);
  const [crDateTo,      setCrDateTo]      = useState(today);
  const [crSummary,     setCrSummary]     = useState([]);
  const [crLoading,     setCrLoading]     = useState(false);
  const [crSortKey,     setCrSortKey]     = useState("display_name");
  const [crSortDir,     setCrSortDir]     = useState("asc");
  const [crDrawer,      setCrDrawer]      = useState(null); // { userId, displayName, type }
  const [crDrawerRows,  setCrDrawerRows]  = useState([]);
  const [crDrawerLoading, setCrDrawerLoading] = useState(false);
  const [crExpandedRow, setCrExpandedRow] = useState(null); // row index expanded in drawer

  const fetchData = useCallback(() => {
    if (selectedReport === "timesheet") {
      if (!dateFrom || !dateTo) return;
      setLoading(true);
      fetch(`${API}/reports/timesheet-entry?date_from=${dateFrom}&date_to=${dateTo}`, { credentials: "include" })
        .then(r => r.json())
        .then((data) => { setTsReportData(data); })
        .catch(() => showToast?.("Failed to load report data", "warn"))
        .finally(() => setLoading(false));
      return;
    }

    if (selectedReport === "monthly-timesheet") {
      if (!monthlyMonth) return;
      setLoading(true);
      fetch(`${API}/reports/monthly-timesheet?month=${monthlyMonth}`, { credentials: "include" })
        .then(r => r.json())
        .then((data) => {
          setMonthlyData({
            date_columns: Array.isArray(data?.date_columns) ? data.date_columns : [],
            rows: Array.isArray(data?.rows) ? data.rows : [],
            summary: data?.summary || {},
            month: data?.month || monthlyMonth,
          });
        })
        .catch(() => showToast?.("Failed to load monthly timesheet report", "warn"))
        .finally(() => setLoading(false));
      return;
    }

    if (selectedReport === "nc") {
      setLoading(true);
      fetch(`${API}/reports/non-compliance-summary?date_from=${dateFrom}&date_to=${dateTo}`, { credentials: "include" })
        .then(r => r.json())
        .then((data) => {
          setNcRows(Array.isArray(data?.users) ? data.users : []);
        })
        .catch(() => showToast?.("Failed to load non-compliance report", "warn"))
        .finally(() => setLoading(false));
      return;
    }

    // efforts-customer and efforts-team are each handled by their own
    // dedicated effects (keyed on effortDrillPath / eftDrillPath) below
    // fetchData, not here — each has its own loading state since every
    // drill level fetches independently.

    if (selectedReport === "efforts-product") {
      setLoading(true);
      fetch(`${API}/reports/efforts-by-product?date_from=${dateFrom}&date_to=${dateTo}`, { credentials: "include" })
        .then(r => r.json())
        .then((data) => {
          setEffortProductData({
            categories: Array.isArray(data?.categories) ? data.categories : [],
            rows: Array.isArray(data?.rows) ? data.rows : [],
            category_totals: data?.category_totals || {},
            grand_total_minutes: Number(data?.grand_total_minutes) || 0,
          });
        })
        .catch(() => showToast?.("Failed to load efforts by product report", "warn"))
        .finally(() => setLoading(false));
    }
  }, [dateFrom, dateTo, monthlyMonth, selectedReport, showToast]);

  // Push one level deeper into the drill chain (Customer -> Project -> Epic
  // -> Team -> Resource). `id` is the UUID for project/epic, or the team
  // name itself for the team level (teams have no separate id) — null for
  // the "No Team"/"No Epic"/"No Project" buckets, same nullable-id
  // convention at every level, so it round-trips as the "none" sentinel
  // rather than the display label itself. `name` is always the
  // human-readable label shown in the breadcrumb.
  const drillInto = useCallback((level, id, name) => {
    setEffortDrillPath((prev) => [...prev, { level, id, name }]);
  }, []);

  // Jump back to any earlier breadcrumb segment — depth is the number of
  // path entries to KEEP (0 = back to Customers).
  const drillToDepth = useCallback((depth) => {
    setEffortDrillPath((prev) => prev.slice(0, depth));
  }, []);

  const EFFORT_LEVEL_NAMES = ["customer", "project", "epic", "team"];

  // Fetches whichever of the 4 non-resource levels effortDrillPath currently
  // points at. Each level's raw response shape differs (customer_name vs
  // project_id/project_name vs epic_id/epic_name vs team_name) — normalized
  // here into one common { key, id, label, sublabel?, total_minutes } shape
  // so the rendering below doesn't need to know which level it's showing.
  useEffect(() => {
    if (selectedReport !== "efforts-customer") return;
    const depth = effortDrillPath.length;
    if (depth >= 4) return; // Resource level — handled by the effect below.

    setEffortLevelLoading(true);
    const params = new URLSearchParams({ date_from: efcDateFrom, date_to: efcDateTo });
    let url = `${API}/reports/efforts-by-customer`;
    if (depth >= 1) {
      params.set("customer_name", effortDrillPath[0].name);
      url = `${API}/reports/efforts-by-customer/projects`;
    }
    if (depth >= 2) {
      params.set("project_id", effortDrillPath[1].id || "none");
      url = `${API}/reports/efforts-by-customer/epics`;
    }
    if (depth >= 3) {
      params.set("epic_id", effortDrillPath[2].id || "none");
      url = `${API}/reports/efforts-by-customer/teams`;
    }

    fetch(`${url}?${params.toString()}`, { credentials: "include" })
      .then(r => r.json())
      .then((data) => {
        const rawRows = Array.isArray(data?.rows) ? data.rows : [];
        const rows = rawRows.map((r) => {
          if (depth === 0) return {
            key: r.customer_name || "Non-mapped", id: r.customer_name || null,
            label: r.customer_name || "Non-mapped", sublabel: r.am_name || null,
            total_minutes: Number(r.total_minutes) || 0,
          };
          if (depth === 1) return {
            key: r.project_id || "none", id: r.project_id || null,
            label: r.project_name, total_minutes: Number(r.total_minutes) || 0,
          };
          if (depth === 2) return {
            key: r.epic_id || "none", id: r.epic_id || null,
            label: r.epic_name, total_minutes: Number(r.total_minutes) || 0,
          };
          return {
            key: r.team_name || "none", id: r.team_name || null,
            label: r.team_name || "No Team", total_minutes: Number(r.total_minutes) || 0,
          };
        });
        setEffortLevelData({ rows, grand_total_minutes: Number(data?.grand_total_minutes) || 0 });
        setEfcScoped(!!data?.scoped);
      })
      .catch(() => showToast?.("Failed to load efforts by customer report", "warn"))
      .finally(() => setEffortLevelLoading(false));
  }, [selectedReport, effortDrillPath, efcDateFrom, efcDateTo, showToast]);

  // Contextual Top-5 sidebar donuts — re-derives which two dimensions (and
  // scope) apply every time the drill path changes, so this depends on
  // effortDrillPath as well as the date range (unlike before, when it was a
  // drill-independent global fetch).
  useEffect(() => {
    if (selectedReport !== "efforts-customer") return;
    const [specA, specB] = efcDonutSpecsForPath(effortDrillPath);
    const fetchDonut = (spec, setState) => {
      setState((prev) => ({ ...prev, title: spec.title, unit: spec.unit, loading: true }));
      const params = new URLSearchParams({
        dimension: spec.dimension,
        date_from: efcDateFrom, date_to: efcDateTo,
        ...spec.scopeParams,
      });
      fetch(`${API}/reports/efforts-by-customer/top?${params.toString()}`, { credentials: "include" })
        .then(r => r.json())
        .then((data) => setState({ title: spec.title, unit: spec.unit, rows: Array.isArray(data?.rows) ? data.rows : [], loading: false }))
        .catch(() => {
          showToast?.(`Failed to load ${spec.title.toLowerCase()}`, "warn");
          setState({ title: spec.title, unit: spec.unit, rows: [], loading: false });
        });
    };
    fetchDonut(specA, setEfcDonutA);
    fetchDonut(specB, setEfcDonutB);
  }, [selectedReport, effortDrillPath, efcDateFrom, efcDateTo, showToast]);

  // Resource level (effortDrillPath.length === 4) — per-user summary.
  useEffect(() => {
    if (selectedReport !== "efforts-customer" || effortDrillPath.length !== 4) return;
    const [customer, project, epic, team] = effortDrillPath;
    const params = new URLSearchParams({
      customer_name: customer.name,
      project_id: project.id || "none",
      epic_id: epic.id || "none",
      team_name: team.id || "none",
      date_from: efcDateFrom, date_to: efcDateTo,
    });
    setEffortDrilldownExpanded({});
    setEffortDrilldownLoading(true);
    fetch(`${API}/reports/efforts-by-customer/entries?${params.toString()}`, { credentials: "include" })
      .then(r => r.json())
      .then((data) => {
        setEffortDrilldownData({
          rows: Array.isArray(data?.rows) ? data.rows : [],
          totals: data?.totals || {},
        });
        setEfcScoped(!!data?.scoped);
      })
      .catch(() => showToast?.("Failed to load efforts drill-down", "warn"))
      .finally(() => setEffortDrilldownLoading(false));
  }, [selectedReport, effortDrillPath, efcDateFrom, efcDateTo, showToast]);

  const fetchEffortDrilldownDetail = useCallback((userId, entryCategory) => {
    if (effortDrillPath.length !== 4) return;
    const [customer, project, epic, team] = effortDrillPath;
    const params = new URLSearchParams({
      customer_name: customer.name,
      project_id: project.id || "none",
      epic_id: epic.id || "none",
      team_name: team.id || "none",
      user_id: userId, date_from: efcDateFrom, date_to: efcDateTo,
    });
    if (entryCategory) params.set("entry_category", entryCategory);
    const key = `${userId}:${entryCategory || "total"}`;
    fetch(`${API}/reports/efforts-by-customer/entries/detail?${params.toString()}`, { credentials: "include" })
      .then(r => r.json())
      .then((data) => {
        setEffortDrilldownExpanded((prev) => (
          prev[key] ? { ...prev, [key]: { loading: false, rows: Array.isArray(data?.rows) ? data.rows : [] } } : prev
        ));
      })
      .catch(() => showToast?.("Failed to load date-wise entries", "warn"));
  }, [effortDrillPath, efcDateFrom, efcDateTo, showToast]);

  const toggleEffortDrilldownExpand = useCallback((userId, entryCategory) => {
    const key = `${userId}:${entryCategory || "total"}`;
    if (effortDrilldownExpanded[key]) {
      // Already open — collapse it, nothing else to open.
      setEffortDrilldownExpanded({});
      return;
    }
    // Only one date-wise breakdown open at a time — opening a new one
    // replaces (rather than merges with) whatever was already expanded.
    setEffortDrilldownExpanded({ [key]: { loading: true, rows: [] } });
    fetchEffortDrilldownDetail(userId, entryCategory);
  }, [effortDrilldownExpanded, fetchEffortDrilldownDetail]);

  // Effort per Team — list-level effect (Team, or in full mode
  // Customer/Project/Epic within a chosen team). Skips entirely once at the
  // mode's final depth — the Resource-level effect below owns that.
  useEffect(() => {
    if (selectedReport !== "efforts-team") return;
    const depth = eftDrillPath.length;
    if (depth >= eftFinalDepth) return;

    setEftLevelLoading(true);
    const params = new URLSearchParams({ date_from: eftDateFrom, date_to: eftDateTo });

    if (depth === 0) {
      fetch(`${API}/reports/efforts-by-team?${params.toString()}`, { credentials: "include" })
        .then(r => r.json())
        .then((data) => {
          const rawRows = Array.isArray(data?.rows) ? data.rows : [];
          // The existing /reports/efforts-by-team endpoint pre-coalesces the
          // null-team bucket to the literal string "Unassigned" in SQL — the
          // one place that endpoint deviates from the raw-nullable-id
          // convention used everywhere else. Translate it back to a null id
          // here (client-side only) so it round-trips as the "none"
          // sentinel to the new team-anchored endpoints below, without
          // touching that endpoint's response shape at all.
          const rows = rawRows.map((r) => ({
            key: r.team_name || "none",
            id: r.team_name === "Unassigned" ? null : r.team_name,
            label: r.team_name || "Unassigned",
            total_minutes: Number(r.total_minutes) || 0,
          }));
          setEftLevelData({ rows, grand_total_minutes: Number(data?.grand_total_minutes) || 0 });
        })
        .catch(() => showToast?.("Failed to load efforts by team report", "warn"))
        .finally(() => setEftLevelLoading(false));
      return;
    }

    const team = eftDrillPath[0];
    params.set("team_name", team.id || "none");

    if (depth === 1) {
      // Customer list within the chosen team. customer_name is a coalesced
      // TEXT column (never null — "Non-mapped" is a real value, not a
      // sentinel), so the row's own name doubles as its id, same convention
      // the root Efforts-by-Customer customer list already uses.
      fetch(`${API}/reports/efforts-by-team/customers?${params.toString()}`, { credentials: "include" })
        .then(r => r.json())
        .then((data) => {
          const rawRows = Array.isArray(data?.rows) ? data.rows : [];
          const rows = rawRows.map((r) => ({
            key: r.customer_name || "Non-mapped",
            id: r.customer_name || null,
            label: r.customer_name || "Non-mapped",
            total_minutes: Number(r.total_minutes) || 0,
          }));
          setEftLevelData({ rows, grand_total_minutes: Number(data?.grand_total_minutes) || 0 });
        })
        .catch(() => showToast?.("Failed to load effort per team drill-down", "warn"))
        .finally(() => setEftLevelLoading(false));
      return;
    }

    // depth 2 (full mode only): Project list within team+customer.
    // depth 3 (full mode only): Epic list within team+customer+project.
    let url = `${API}/reports/efforts-by-team/projects`;
    if (depth === 2) {
      params.set("customer_name", eftDrillPath[1].id || "Non-mapped");
    } else {
      params.set("project_id", eftDrillPath[2].id || "none");
      url = `${API}/reports/efforts-by-team/epics`;
    }

    fetch(`${url}?${params.toString()}`, { credentials: "include" })
      .then(r => r.json())
      .then((data) => {
        const rawRows = Array.isArray(data?.rows) ? data.rows : [];
        const rows = rawRows.map((r) => (
          depth === 2
            ? { key: r.project_id || "none", id: r.project_id || null, label: r.project_name, total_minutes: Number(r.total_minutes) || 0 }
            : { key: r.epic_id || "none",    id: r.epic_id || null,    label: r.epic_name,    total_minutes: Number(r.total_minutes) || 0 }
        ));
        setEftLevelData({ rows, grand_total_minutes: Number(data?.grand_total_minutes) || 0 });
      })
      .catch(() => showToast?.("Failed to load effort per team drill-down", "warn"))
      .finally(() => setEftLevelLoading(false));
  }, [selectedReport, eftMode, eftDrillPath, eftFinalDepth, eftDateFrom, eftDateTo, showToast]);

  // Effort per Team — sidebar donuts, re-derived on every drill-path change.
  useEffect(() => {
    if (selectedReport !== "efforts-team") return;
    const [specA, specB] = eftDonutSpecsForPath(eftMode, eftDrillPath);
    const fetchDonut = (spec, setState) => {
      setState((prev) => ({ ...prev, title: spec.title, unit: spec.unit, loading: true }));
      const params = new URLSearchParams({
        dimension: spec.dimension,
        date_from: eftDateFrom, date_to: eftDateTo,
        ...spec.scopeParams,
      });
      fetch(`${API}/reports/efforts-by-customer/top?${params.toString()}`, { credentials: "include" })
        .then(r => r.json())
        .then((data) => setState({ title: spec.title, unit: spec.unit, rows: Array.isArray(data?.rows) ? data.rows : [], loading: false }))
        .catch(() => {
          showToast?.(`Failed to load ${spec.title.toLowerCase()}`, "warn");
          setState({ title: spec.title, unit: spec.unit, rows: [], loading: false });
        });
    };
    fetchDonut(specA, setEftDonutA);
    fetchDonut(specB, setEftDonutB);
  }, [selectedReport, eftMode, eftDrillPath, eftDateFrom, eftDateTo, showToast]);

  // Effort per Team — Resource level (final depth, either mode) — per-user summary.
  useEffect(() => {
    if (selectedReport !== "efforts-team" || eftDrillPath.length !== eftFinalDepth) return;
    const team = eftDrillPath[0];
    const params = new URLSearchParams({
      team_name: team.id || "none",
      date_from: eftDateFrom, date_to: eftDateTo,
    });
    if (eftMode === "full") {
      params.set("project_id", eftDrillPath[2].id || "none");
      params.set("epic_id", eftDrillPath[3].id || "none");
    }
    setEftEntriesExpanded({});
    setEftEntriesLoading(true);
    fetch(`${API}/reports/efforts-by-team/entries?${params.toString()}`, { credentials: "include" })
      .then(r => r.json())
      .then((data) => {
        setEftEntriesData({
          rows: Array.isArray(data?.rows) ? data.rows : [],
          totals: data?.totals || {},
        });
      })
      .catch(() => showToast?.("Failed to load effort per team drill-down", "warn"))
      .finally(() => setEftEntriesLoading(false));
  }, [selectedReport, eftMode, eftDrillPath, eftFinalDepth, eftDateFrom, eftDateTo, showToast]);

  const fetchEftEntriesDetail = useCallback((userId, entryCategory) => {
    if (eftDrillPath.length !== eftFinalDepth) return;
    const team = eftDrillPath[0];
    const params = new URLSearchParams({
      team_name: team.id || "none",
      user_id: userId, date_from: eftDateFrom, date_to: eftDateTo,
    });
    if (eftMode === "full") {
      params.set("project_id", eftDrillPath[2].id || "none");
      params.set("epic_id", eftDrillPath[3].id || "none");
    }
    if (entryCategory) params.set("entry_category", entryCategory);
    const key = `${userId}:${entryCategory || "total"}`;
    fetch(`${API}/reports/efforts-by-team/entries/detail?${params.toString()}`, { credentials: "include" })
      .then(r => r.json())
      .then((data) => {
        setEftEntriesExpanded((prev) => (
          prev[key] ? { ...prev, [key]: { loading: false, rows: Array.isArray(data?.rows) ? data.rows : [] } } : prev
        ));
      })
      .catch(() => showToast?.("Failed to load date-wise entries", "warn"));
  }, [eftMode, eftDrillPath, eftFinalDepth, eftDateFrom, eftDateTo, showToast]);

  const toggleEftEntriesExpand = useCallback((userId, entryCategory) => {
    const key = `${userId}:${entryCategory || "total"}`;
    if (eftEntriesExpanded[key]) {
      setEftEntriesExpanded({});
      return;
    }
    setEftEntriesExpanded({ [key]: { loading: true, rows: [] } });
    fetchEftEntriesDetail(userId, entryCategory);
  }, [eftEntriesExpanded, fetchEftEntriesDetail]);

  // User level: total effort per user, ranked — mirrors the customer-level
  // chart's "total effort" pattern one level down. No further drill target
  // was specified for this level, so bars are informational only.
  const effortDrilldownChartRows = effortDrilldownData.rows
    .map((r) => ({ label: r.user_name || "—", value: Number(r.total_minutes) || 0, user_id: r.user_id }))
    .sort((a, b) => b.value - a.value);

  // Sidebar donut rows — response shape from /top is already uniform
  // ({id, label, sublabel, total_minutes}) regardless of which dimension
  // was requested, so both donuts map the same way.
  const efcDonutRows = (donut) => donut.rows.map((r) => ({
    key: r.id, label: r.label, sublabel: r.sublabel || null,
    value: Number(r.total_minutes) || 0,
  }));

  const fetchCrSummary = useCallback(() => {
    if (!crDateFrom || !crDateTo) return;
    setCrLoading(true);
    fetch(`${API}/reports/compliance-summary?date_from=${crDateFrom}&date_to=${crDateTo}`, { credentials: "include" })
      .then(r => r.json())
      .then(data => setCrSummary(Array.isArray(data) ? data : []))
      .catch(() => showToast?.("Failed to load compliance summary", "warn"))
      .finally(() => setCrLoading(false));
  }, [crDateFrom, crDateTo, showToast]);

  useEffect(() => {
    if (selectedReport === "compliance-records") fetchCrSummary();
  }, [selectedReport, fetchCrSummary]);

  const eeActiveFilterCount = countActiveFilters(eeFilters);

  // ChoiceFilterPopover field definitions — same {key,label,type,options}
  // shape every other page using this shared component builds. Epic Start
  // Date carries EE_PERIOD_PRESETS as field.presets (the generic quick-range
  // capability added to ChoiceFilterPopover); Epic End Date has none, same
  // as before this migration (presets only ever applied to start_from/to).
  const eeChoiceFields = useMemo(() => [
    { key: "status_in",      label: "Status",         type: "choice", options: EE_STATUS_OPTS },
    { key: "customer_in",    label: "Customer",       type: "choice", options: eeCustomerOpts },
    { key: "epic_name_in",   label: "Epic Name",      type: "choice", options: eeEpicOpts },
    { key: "pace_status_in", label: "Pace",           type: "choice", options: EE_PACE_FILTER_OPTS },
    { key: "am_name_in",     label: "AM",             type: "choice", options: eeAmOpts },
    { key: "start_date",     label: "Epic Start Date", type: "daterange", presets: EE_PERIOD_PRESETS },
    { key: "end_date",       label: "Epic End Date",   type: "daterange" },
  ], [eeCustomerOpts, eeEpicOpts, eeAmOpts]);

  // Client-side filter over the once-fetched eeRows — see fetchEpicEffort's
  // comment for why the network fetch is now unfiltered. getFieldValue maps
  // ChoiceFilterPopover's field keys onto the actual row property names
  // where they differ (status_in -> epic_status, etc.).
  const eeVisibleRows = useMemo(() => buildFilteredRows({
    rows: eeRows,
    appliedFilters: eeFilters,
    filterMode: eeFilterMode,
    getFieldValue: (row, key) => {
      if (key === "status_in") return row.epic_status;
      if (key === "customer_in") return row.customer_name;
      if (key === "epic_name_in") return row.epic_name;
      if (key === "pace_status_in") return row.pace_status;
      if (key === "am_name_in") return row.am_name;
      if (key === "start_date") return row.start_date;
      if (key === "end_date") return row.end_date;
      return row[key];
    },
  }), [eeRows, eeFilters, eeFilterMode]);

  useEffect(() => {
    if (selectedReport !== "epic-effort") return;
    Promise.all([
      fetch(`${API}/customers`, { credentials: "include" }).then(r => r.json()).catch(() => []),
      fetch(`${API}/users/dropdown`, { credentials: "include" }).then(r => r.json()).catch(() => []),
      fetch(`${API}/epics?include_closed=true`, { credentials: "include" }).then(r => r.json()).catch(() => []),
    ]).then(([customers, users, epics]) => {
      setEeCustomerOpts(
        (Array.isArray(customers) ? customers : [])
          .filter(c => c.customer_name)
          .map(c => ({ value: c.customer_name, label: c.customer_name }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );
      setEeAmOpts(
        (Array.isArray(users) ? users : [])
          .filter(u => u.display_name)
          .map(u => ({ value: u.display_name, label: u.display_name }))
      );
      const epicList = Array.isArray(epics) ? epics : [];
      setEeEpicOpts(
        epicList
          .filter(e => e.epic_name)
          .map(e => ({ value: e.epic_name, label: e.epic_code ? `${e.epic_code} — ${e.epic_name}` : e.epic_name }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    });
  }, [selectedReport]);

  // Fetches every epic (every status, every customer) ONCE — filtering is
  // now client-side (buildFilteredRows below), same architecture Tasks/
  // Defects/Timesheet already use for their own ChoiceFilterPopover-style
  // advanced filters (fetch broadly, re-filter instantly on "Find" with no
  // network round-trip). Epic count is small (a few hundred rows, checked
  // against production), so fetching unfiltered is cheap; this also means
  // eeFilters no longer drives what's fetched, only what's displayed.
  const fetchEpicEffort = useCallback(() => {
    setEeLoading(true);
    fetch(`${API}/reports/epic-effort`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        setEeTeams(Array.isArray(data?.teams) ? data.teams : []);
        setEeRows(Array.isArray(data?.rows) ? data.rows : []);
        setEeScoped(!!data?.scoped);
      })
      .catch(() => showToast?.("Failed to load Epic Effort report", "warn"))
      .finally(() => setEeLoading(false));
  }, [showToast]);

  // Auto-fetch once per visit to this report (no more manual "Fetch"
  // button) — ref-guarded rather than gated on eeLoading so this doesn't
  // fall into the fetch-once-per-mount/StrictMode-double-invoke trap (see
  // CLAUDE.md's note on the Defects Executive Summary tab's identical fix).
  // Resets on exit so re-entering the report re-fetches fresh data.
  const eeAutoFetchedRef = useRef(false);
  useEffect(() => {
    if (selectedReport !== "epic-effort") {
      eeAutoFetchedRef.current = false;
      return;
    }
    if (eeAutoFetchedRef.current) return;
    eeAutoFetchedRef.current = true;
    fetchEpicEffort();
  }, [selectedReport, fetchEpicEffort]);

  // Single shared floating tooltip for every row's team-effort bar segments —
  // mirrors LifecycleBar's own tipRef/innerHTML approach (DefectsViewV2Page.jsx)
  // rather than per-row React state, since there can be 70+ rows on screen.
  // position: fixed + getBoundingClientRect() so one tooltip element works
  // for every row regardless of scroll position, with no per-row wrapper math.
  const eeTeamTipRef = useRef(null);
  const showEeTeamTip = useCallback((el, seg) => {
    const tip = eeTeamTipRef.current;
    if (!tip) return;
    tip.innerHTML = `
      <div class="tt-title">${escHtml(seg.name)}</div>
      <div class="tt-row"><span class="sw" style="background:${escHtml(seg.color)}"></span><span class="k">Hours</span><span class="v">${escHtml(fmtHoursDecimal(seg.actual_hours))}</span></div>
      <div class="tt-row"><span class="k">Share of Actual</span><span class="v">${seg.pct}%</span></div>
    `;
    tip.classList.add("on");
    const rect = el.getBoundingClientRect();
    // Centered on the segment by default (CSS does translateX(-50%)), but
    // clamped so the tooltip's own measured width never pushes it past the
    // viewport edge — a segment near the row's right edge previously
    // centered the tip there anyway, running it half off-screen.
    const tipRect = tip.getBoundingClientRect();
    const half = tipRect.width / 2;
    const margin = 8;
    const center = rect.left + rect.width / 2;
    const clampedCenter = Math.max(half + margin, Math.min(center, window.innerWidth - half - margin));
    tip.style.left = `${clampedCenter}px`;
    tip.style.top = `${rect.top}px`;
  }, []);
  const hideEeTeamTip = useCallback(() => {
    if (eeTeamTipRef.current) eeTeamTipRef.current.classList.remove("on");
  }, []);

  const openCrDrawer = (userId, displayName, type) => {
    setCrDrawer({ userId, displayName, type });
    setCrDrawerRows([]);
    setCrExpandedRow(null);
    setCrDrawerLoading(true);
    fetch(
      `${API}/reports/compliance-history?user_id=${userId}&compliance_type=${type}&date_from=${crDateFrom}&date_to=${crDateTo}`,
      { credentials: "include" }
    )
      .then(r => r.json())
      .then(data => setCrDrawerRows(Array.isArray(data) ? data : []))
      .catch(() => showToast?.("Failed to load compliance detail", "warn"))
      .finally(() => setCrDrawerLoading(false));
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateNcFilter = (key, patch) => {
    setNcFilters((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...patch,
      },
    }));
  };
  const activeNcFilterCount = Object.values(ncFilters).filter((f) => String(f?.value || "").trim() !== "").length;
  const clearNcFilters = () => {
    setNcFilters({
      avg_minutes: { op: ">=", value: "" },
      overdue_tasks: { op: ">", value: "" },
      allocation_hours: { op: "<", value: "" },
    });
  };

  // Compliance records derived
  const crSorted = [...crSummary].sort((a, b) => {
    let aVal, bVal;
    if (crSortKey === "display_name") {
      aVal = a.display_name || ""; bVal = b.display_name || "";
      return crSortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    if (crSortKey === "overall") {
      aVal = a.overall_rate || 0; bVal = b.overall_rate || 0;
    } else {
      aVal = a[crSortKey]?.rate || 0; bVal = b[crSortKey]?.rate || 0;
    }
    return crSortDir === "asc" ? aVal - bVal : bVal - aVal;
  });

  const crOverallStats = CR_TYPES.reduce((acc, t) => {
    const total   = crSummary.reduce((s, u) => s + (u[t]?.total_days || 0), 0);
    const compliant = crSummary.reduce((s, u) => s + (u[t]?.compliant_days || 0), 0);
    acc[t] = { compliant, total, rate: total > 0 ? Math.round(compliant / total * 100) : 0 };
    return acc;
  }, {});

  const toggleCrSort = (key) => {
    if (crSortKey === key) setCrSortDir(d => d === "asc" ? "desc" : "asc");
    else { setCrSortKey(key); setCrSortDir("desc"); }
  };

  const downloadCrCsv = () => {
    const headers = ["User", "Timesheet %", "Timesheet Days", "Tasks %", "Tasks Days", "Allocation %", "Allocation Days", "Defects %", "Defects Days", "Overall %"];
    const rows = crSorted.map(u => [
      u.display_name || u.user_name,
      fmtRate(u.timesheet?.rate || 0),
      `${u.timesheet?.compliant_days || 0}/${u.timesheet?.total_days || 0}`,
      fmtRate(u.task?.rate || 0),
      `${u.task?.compliant_days || 0}/${u.task?.total_days || 0}`,
      fmtRate(u.allocation?.rate || 0),
      `${u.allocation?.compliant_days || 0}/${u.allocation?.total_days || 0}`,
      fmtRate(u.defect?.rate || 0),
      `${u.defect?.compliant_days || 0}/${u.defect?.total_days || 0}`,
      fmtRate(u.overall_rate || 0),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    saveAs(blob, `compliance_records_${crDateFrom}_to_${crDateTo}.csv`);
  };

  // Derived from consolidated backend response
  const allRows        = tsReportData?.rows        ?? [];
  const rawList        = tsReportData?.raw_entries ?? [];
  const summary        = tsReportData?.summary     ?? {};
  const excludedIds    = new Set(allRows.filter(r => r.is_excluded).map(r => r.user_id));
  const inScopeRows    = allRows.filter(r => !r.is_excluded);
  const inScopeRawList = rawList.filter(r => !excludedIds.has(r.user_id));
  const displayRows    = showExcluded
    ? [...inScopeRows, ...allRows.filter(r => r.is_excluded)]
    : inScopeRows;
  const monthlyDateColumns = Array.isArray(monthlyData?.date_columns) ? monthlyData.date_columns : [];
  const monthlyRows = Array.isArray(monthlyData?.rows) ? monthlyData.rows : [];
  const monthlySummary = monthlyData?.summary || {};

  const tsFilterActiveCount = ((dateFrom || dateTo) ? 1 : 0) + (showExcluded ? 1 : 0);

  const dateLabel = dateFrom === dateTo
    ? fmtDisplayDate(dateFrom)
    : `${fmtDisplayDate(dateFrom)} – ${fmtDisplayDate(dateTo)}`;

  const efcDateLabel = efcDateFrom === efcDateTo
    ? fmtDisplayDate(efcDateFrom)
    : `${fmtDisplayDate(efcDateFrom)} – ${fmtDisplayDate(efcDateTo)}`;

  const getDatesInRange = () => {
    const dates = [];
    const cur = new Date(dateFrom + "T00:00:00");
    const end = new Date(dateTo   + "T00:00:00");
    while (cur <= end) {
      const day = cur.getDay();
      if (day !== 0 && day !== 6) dates.push(toLocalISO(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return dates;
  };

  const fmtDateHeader = (iso) => {
    const d = new Date(iso + "T00:00:00");
    const day = String(d.getDate()).padStart(2, "0");
    const mon = d.toLocaleDateString("en-GB", { month: "short" });
    const yr  = String(d.getFullYear()).slice(2);
    return `${day}-${mon}-${yr}`;
  };

  const timesheetBg = (totalMins) => {
    if (totalMins >= 8 * 60) return null;
    if (totalMins > 7 * 60)  return "#fff9c4"; // yellow
    if (totalMins > 4 * 60)  return "#ffe0b2"; // orange
    return "#ffcdd2";                           // red
  };

  const officeMismatchStyle = (officeMins, timesheetMins) => {
    if ((Number(officeMins) || 0) < (Number(timesheetMins) || 0)) {
      return { color: "#b91c1c", fontWeight: 700 };
    }
    return {};
  };

  const filteredNcRows = ncRows.filter((row) =>
    matchesNumericFilter(Number(row.avg_minutes) || 0, ncFilters.avg_minutes) &&
    matchesNumericFilter(Number(row.overdue_tasks) || 0, ncFilters.overdue_tasks) &&
    matchesNumericFilter(Number(row.allocation_hours) || 0, ncFilters.allocation_hours)
  );

  const downloadNcExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Non-Compliance");

    const headers = ["S.No", "User", "Avg Time entry", "Overdue Tasks", "Allocation Hours"];
    const hdrRow = ws.addRow(headers);
    hdrRow.eachCell(cell => {
      cell.font = { bold: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEDE7F6" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    filteredNcRows.forEach((row, i) => {
      const exRow = ws.addRow([
        i + 1,
        row.user_display_name || row.user_name || "—",
        fmtTime(Number(row.avg_minutes) || 0),
        Number(row.overdue_tasks) || 0,
        fmtHoursDecimal(row.allocation_hours),
      ]);

      exRow.getCell(1).alignment = { horizontal: "center" };
      exRow.getCell(3).alignment = { horizontal: "center" };
      exRow.getCell(4).alignment = { horizontal: "center" };
      exRow.getCell(5).alignment = { horizontal: "center" };
    });

    ws.getColumn(1).width = 8;
    ws.getColumn(2).width = 26;
    ws.getColumn(3).width = 18;
    ws.getColumn(4).width = 16;
    ws.getColumn(5).width = 18;

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `non_compliance_report_${dateFrom}_to_${dateTo}.xlsx`
    );
  };

  // Column header label for whichever of the 4 non-resource levels is showing.
  const EFFORT_LEVEL_COLUMN_LABELS = ["Customer", "Project", "Epic", "Team"];
  const effortLevelColumnLabel = EFFORT_LEVEL_COLUMN_LABELS[effortDrillPath.length] || "Customer";

  // Chart rows for the current level — same {label, sublabel, value} shape
  // EfcBarChart already expects, at every depth. `id` carried through so
  // onBarClick can use it directly rather than looking a row back up by
  // (possibly non-unique) label.
  const effortLevelChartRows = effortLevelData.rows
    .map((r) => ({ id: r.id, label: r.label || "—", sublabel: r.sublabel || null, value: r.total_minutes }))
    .sort((a, b) => b.value - a.value);

  const toggleEffortLevelSort = (key) => {
    if (effortLevelSortKey === key) setEffortLevelSortDir(d => d === "asc" ? "desc" : "asc");
    else { setEffortLevelSortKey(key); setEffortLevelSortDir("desc"); }
  };

  const sortedEffortLevelRows = [...effortLevelData.rows].sort((a, b) => {
    if (!effortLevelSortKey) return 0;
    if (effortLevelSortKey === "label") {
      const aName = a.label || "—";
      const bName = b.label || "—";
      // "No Project"/"No Epic"/"No Team"/"Non-mapped" always sort last.
      const aOther = !a.id;
      const bOther = !b.id;
      if (aOther && !bOther) return 1;
      if (bOther && !aOther) return -1;
      return effortLevelSortDir === "asc" ? aName.localeCompare(bName) : bName.localeCompare(aName);
    }
    const aVal = a.total_minutes;
    const bVal = b.total_minutes;
    return effortLevelSortDir === "asc" ? aVal - bVal : bVal - aVal;
  });

  // Effort per Team — same derived-rows pattern as the Customer-report block
  // above, just re-scoped to eft* state and the Team/Project/Epic labels.
  const EFT_LEVEL_COLUMN_LABELS = ["Team", "Customer", "Project", "Epic"];
  const eftLevelColumnLabel = EFT_LEVEL_COLUMN_LABELS[eftDrillPath.length] || "Team";

  const eftLevelChartRows = eftLevelData.rows
    .map((r) => ({ id: r.id, label: r.label || "—", value: r.total_minutes }))
    .sort((a, b) => b.value - a.value);

  const toggleEftLevelSort = (key) => {
    if (eftLevelSortKey === key) setEftLevelSortDir(d => d === "asc" ? "desc" : "asc");
    else { setEftLevelSortKey(key); setEftLevelSortDir("desc"); }
  };

  const sortedEftLevelRows = [...eftLevelData.rows].sort((a, b) => {
    if (!eftLevelSortKey) return 0;
    if (eftLevelSortKey === "label") {
      const aName = a.label || "—";
      const bName = b.label || "—";
      const aOther = !a.id;
      const bOther = !b.id;
      if (aOther && !bOther) return 1;
      if (bOther && !aOther) return -1;
      return eftLevelSortDir === "asc" ? aName.localeCompare(bName) : bName.localeCompare(aName);
    }
    const aVal = a.total_minutes;
    const bVal = b.total_minutes;
    return eftLevelSortDir === "asc" ? aVal - bVal : bVal - aVal;
  });

  const eftEntriesChartRows = eftEntriesData.rows
    .map((r) => ({ label: r.user_name || "—", value: Number(r.total_minutes) || 0, user_id: r.user_id }))
    .sort((a, b) => b.value - a.value);

  const eftDateLabel = eftDateFrom === eftDateTo
    ? fmtDisplayDate(eftDateFrom)
    : `${fmtDisplayDate(eftDateFrom)} – ${fmtDisplayDate(eftDateTo)}`;

  const downloadCustomerEffortsExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const sheetName = ["Efforts by Customer", ...effortDrillPath.map(p => p.name)].join(" — ").slice(0, 31);
    const ws = wb.addWorksheet(sheetName);
    const headers = ["S.No", effortLevelColumnLabel, "Total"];
    const hdrRow = ws.addRow(headers);
    hdrRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8F5E9" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    effortLevelData.rows.forEach((row, i) => {
      const exRow = ws.addRow([i + 1, row.label || "—", fmtTime(row.total_minutes)]);
      exRow.getCell(1).alignment = { horizontal: "center" };
      exRow.getCell(3).alignment = { horizontal: "center" };
    });

    const totalRow = ws.addRow(["", "Total", fmtTime(Number(effortLevelData.grand_total_minutes) || 0)]);
    totalRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } };
    });

    ws.getColumn(1).width = 8;
    ws.getColumn(2).width = 32;
    ws.getColumn(3).width = 14;

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `efforts_by_customer_${effortDrillPath.map(p => p.name).join("_") || "all"}_${efcDateFrom}_to_${efcDateTo}.xlsx`
    );
  };

  // Mirrors downloadCustomerEffortsExcel's 3-column shape exactly — the list
  // level's on-screen table is the same S.No/label/Total shape now, having
  // retired the old Tasks/Defects/Meetings/Others matrix (that breakdown
  // moved to the Resource level's EFD_CATEGORY_COLUMNS, same as Efforts by
  // Customer). List-level export only — Efforts by Customer's own Resource
  // level has no export button either.
  const downloadTeamEffortsExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Efforts per Team");
    const headers = ["S.No", eftLevelColumnLabel, "Total"];
    const hdrRow = ws.addRow(headers);
    hdrRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF3E0" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    eftLevelData.rows.forEach((row, i) => {
      const exRow = ws.addRow([i + 1, row.label || "—", fmtTime(row.total_minutes)]);
      exRow.getCell(1).alignment = { horizontal: "center" };
      exRow.getCell(3).alignment = { horizontal: "center" };
    });

    const totalRow = ws.addRow(["", "Total", fmtTime(Number(eftLevelData.grand_total_minutes) || 0)]);
    totalRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } };
    });

    ws.getColumn(1).width = 8;
    ws.getColumn(2).width = 32;
    ws.getColumn(3).width = 14;

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `efforts_per_team_${eftDateFrom}_to_${eftDateTo}.xlsx`
    );
  };

  const downloadProductEffortsExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Efforts by Product");
    const headers = ["S.No", "Product", ...effortProductData.categories, "Total"];
    const hdrRow = ws.addRow(headers);
    hdrRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE3F2FD" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    effortProductData.rows.forEach((row, i) => {
      const exRow = ws.addRow([
        i + 1,
        row.product_name || "Non-mapped",
        ...effortProductData.categories.map((category) => fmtTime(Number(row.category_minutes?.[category]) || 0)),
        fmtTime(Number(row.total_minutes) || 0),
      ]);
      exRow.getCell(1).alignment = { horizontal: "center" };
      for (let col = 3; col <= headers.length; col += 1) {
        exRow.getCell(col).alignment = { horizontal: "center" };
      }
    });

    const totalRow = ws.addRow([
      "",
      "Total",
      ...effortProductData.categories.map((category) => fmtTime(Number(effortProductData.category_totals?.[category]) || 0)),
      fmtTime(Number(effortProductData.grand_total_minutes) || 0),
    ]);
    totalRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } };
    });

    ws.getColumn(1).width = 8;
    ws.getColumn(2).width = 28;
    for (let col = 3; col <= headers.length; col += 1) ws.getColumn(col).width = 14;

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `efforts_by_product_${dateFrom}_to_${dateTo}.xlsx`
    );
  };

  const buildDetailRows = (dRows) => {
    const userMap = {};
    rawList.forEach(r => {
      const uid = r.user_id || r.user_name;
      if (!uid) return;
      if (!userMap[uid]) userMap[uid] = { user_id: uid, display_name: r.user_display_name || r.user_name || "—", byDate: {} };
      const d = r.report_date?.slice(0, 10);
      if (!d) return;
      if (!userMap[uid].byDate[d]) userMap[uid].byDate[d] = { taskIds: new Set(), defectIds: new Set(), totalMins: 0, officeMins: 0 };
      userMap[uid].byDate[d].totalMins += (Number(r.hours_spent) || 0) * 60 + (Number(r.minutes_spent) || 0);
      if (r.ref_type === "task"   && r.ref_id) userMap[uid].byDate[d].taskIds.add(r.ref_id);
      if (r.ref_type === "defect" && r.ref_id) userMap[uid].byDate[d].defectIds.add(r.ref_id);
    });
    const merged = (Array.isArray(dRows) ? dRows : []).map((r) => {
      const byDate = userMap[r.user_id]?.byDate || {};
      const officeByDate = r.office_by_date || {};
      Object.entries(officeByDate).forEach(([d, mins]) => {
        if (!byDate[d]) byDate[d] = { taskIds: new Set(), defectIds: new Set(), totalMins: 0, officeMins: 0 };
        byDate[d].officeMins = (Number(byDate[d].officeMins) || 0) + (Number(mins) || 0);
      });
      return {
        user_id: r.user_id,
        display_name: r.display_name || "—",
        byDate,
      };
    });
    return merged.sort((a, b) => a.display_name.localeCompare(b.display_name));
  };

  const timesheetColor = (totalMins) => {
    if (totalMins >= 8 * 60)              return null;
    if (totalMins > 7 * 60)              return "FFFFFF00"; // Yellow
    if (totalMins > 4 * 60)              return "FFFFA500"; // Orange
    return "FFFF0000";                                       // Red
  };

  const downloadDetails = async () => {
    const dates = getDatesInRange();
    const users = buildDetailRows(displayRows);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Details");

    // Row 1 — date group headers
    const hdr1 = ["", ""];
    dates.forEach(d => hdr1.push(fmtDateHeader(d), "", "", ""));
    ws.addRow(hdr1);

    // Row 2 — column headers
    const hdr2 = ["S.No", "User"];
    dates.forEach(() => hdr2.push("Tasks", "Defects", "Timesheet", "Hrs in Off"));
    ws.addRow(hdr2);

    // Style header rows
    [1, 2].forEach(rn => {
      ws.getRow(rn).eachCell(cell => {
        cell.font = { bold: true };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" }, bottom: { style: "thin" },
          left: { style: "thin" }, right: { style: "thin" },
        };
      });
    });

    // Merge date header cells in row 1
    dates.forEach((_, i) => {
      const startCol = 3 + i * 4; // 1-based
      ws.mergeCells(1, startCol, 1, startCol + 3);
    });

    // Data rows
    users.forEach((u, i) => {
      const rowData = [i + 1, u.display_name];
      dates.forEach(d => {
        const dd = u.byDate[d];
        rowData.push(dd ? dd.taskIds.size  || 0 : 0);
        rowData.push(dd ? dd.defectIds.size || 0 : 0);
        rowData.push(minsToExcelDuration(dd ? dd.totalMins : 0));
        rowData.push(minsToExcelDuration(dd ? (Number(dd.officeMins) || 0) : 0));
      });
      const exRow = ws.addRow(rowData);

      // Apply color to each Timesheet cell (every 4th col in each date-group).
      dates.forEach((d, di) => {
        const dd = u.byDate[d];
        const mins = dd ? dd.totalMins : 0;
        const argb = timesheetColor(mins);
        const colIdx = 3 + di * 4 + 2; // 1-based: Tasks=3+di*4, Defects=+1, Timesheet=+2
        const cell = exRow.getCell(colIdx);
        cell.numFmt = EXCEL_DURATION_FMT;
        if (argb) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb } };
        }
        cell.alignment = { horizontal: "center" };
        const officeColIdx = 3 + di * 4 + 3;
        const officeCell = exRow.getCell(officeColIdx);
        officeCell.numFmt = EXCEL_DURATION_FMT;
        officeCell.alignment = { horizontal: "center" };
      });

      exRow.getCell(1).alignment = { horizontal: "center" };
    });

    // Column widths
    ws.getColumn(1).width = 6;
    ws.getColumn(2).width = 22;
    dates.forEach((_, i) => {
      ws.getColumn(3 + i * 4).width     = 8;  // Tasks
      ws.getColumn(3 + i * 4 + 1).width = 10; // Defects
      ws.getColumn(3 + i * 4 + 2).width = 12; // Timesheet
      ws.getColumn(3 + i * 4 + 3).width = 12; // Hrs in Off
    });

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `report_details_${dateFrom}_to_${dateTo}.xlsx`);
  };

  const downloadExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Report");

    const headers = ["S.No", "User", "Tasks", "Defects", "Meetings", "Timesheet", "Avg. Hrs", "Hrs in Off"];
    const hdrRow = ws.addRow(headers);
    hdrRow.eachCell(cell => {
      cell.font = { bold: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
    });

    displayRows.forEach((row, i) => {
      const avgMins = row.avg_mins_per_day || 0;
      const exRow = ws.addRow([
        i + 1,
        row.display_name + (row.is_excluded ? " [EXCLUDED]" : ""),
        row.task_ref_count   || 0,
        row.defect_ref_count || 0,
        row.meeting_count    || 0,
        minsToExcelDuration(row.total_mins || 0),
        minsToExcelDuration(avgMins),
        minsToExcelDuration(row.office_mins || 0),
      ]);
      // Color Avg. Hrs cell
      const argb = timesheetColor(avgMins);
      exRow.getCell(6).numFmt = EXCEL_DURATION_FMT;
      exRow.getCell(7).numFmt = EXCEL_DURATION_FMT;
      exRow.getCell(8).numFmt = EXCEL_DURATION_FMT;
      if (argb) {
        exRow.getCell(7).fill = { type: "pattern", pattern: "solid", fgColor: { argb } };
        exRow.getCell(7).font = { bold: true };
      }
      exRow.getCell(1).alignment = { horizontal: "center" };
      [3, 4, 5, 6, 7, 8].forEach(c => { exRow.getCell(c).alignment = { horizontal: "center" }; });
    });

    ws.getColumn(1).width = 6;
    ws.getColumn(2).width = 24;
    ws.getColumn(3).width = 8;
    ws.getColumn(4).width = 10;
    ws.getColumn(5).width = 10;
    ws.getColumn(6).width = 12;
    ws.getColumn(7).width = 12;
    ws.getColumn(8).width = 14;

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `report_${dateFrom}_to_${dateTo}.xlsx`
    );
  };

  const downloadEpicEffortExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Epic Effort");

    const fixedHeaders = ["S.No", "Customer", "Epic Code", "Epic Name", "Status", "AM", "Start Date", "End Date", "Epic Effort (h)", "Actual Effort (h)"];
    const teamHeaders  = eeTeams.map(t => [`${t} Effort (h)`, `${t} %`]).flat();
    const headers = [...fixedHeaders, ...teamHeaders];

    const hdrRow = ws.addRow(headers);
    hdrRow.eachCell(cell => {
      cell.font = { bold: true };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
    });

    eeVisibleRows.forEach((row, i) => {
      const teamCells = eeTeams.map(t => {
        const td = row.team_hours?.[t] || { actual_hours: 0, pct: 0 };
        return [Number(td.actual_hours).toFixed(2), `${Number(td.pct).toFixed(1)}%`];
      }).flat();
      ws.addRow([
        i + 1,
        row.customer_name,
        row.epic_code,
        row.epic_name,
        row.epic_status,
        row.am_name,
        row.start_date,
        row.end_date,
        Number(row.estimated_hours).toFixed(2),
        Number(row.actual_hours).toFixed(2),
        ...teamCells,
      ]);
    });

    ws.getColumn(1).width = 5;
    ws.getColumn(2).width = 20;
    ws.getColumn(3).width = 12;
    ws.getColumn(4).width = 28;
    ws.getColumn(5).width = 16;
    ws.getColumn(6).width = 18;
    ws.getColumn(7).width = 12;
    ws.getColumn(8).width = 12;
    ws.getColumn(9).width = 14;
    ws.getColumn(10).width = 14;
    eeTeams.forEach((_, ti) => {
      ws.getColumn(11 + ti * 2).width = 14;
      ws.getColumn(12 + ti * 2).width = 8;
    });

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `epic_effort_report.xlsx`
    );
  };

  const monthlyCellValue = (row, date) => {
    const leaveDates = Array.isArray(row?.leave_dates) ? row.leave_dates : [];
    if (leaveDates.includes(date)) return "L";
    const val = row?.daily_hours?.[date];
    return val === null || val === undefined ? "--" : Number(val).toFixed(1);
  };

  const monthlyCellStyle = (row, date) => {
    const leaveDates = Array.isArray(row?.leave_dates) ? row.leave_dates : [];
    if (leaveDates.includes(date)) return { background: "#eceff1", color: "#607d8b", fontWeight: 700 };
    const val = row?.daily_hours?.[date];
    if (val === null || val === undefined) return { color: "#78909c" };
    const hours = Number(val) || 0;
    if (hours < 4) return { background: "#ffebee", color: "#c62828", fontWeight: 700 };
    if (hours < 8) return { background: "#fff8e1", color: "#b7791f", fontWeight: 700 };
    return {};
  };

  const downloadMonthlyTimesheetExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Monthly Timesheet");
    const monthLabel = fmtMonthLabel(monthlyData?.month || monthlyMonth);
    const summaryHeaders = [
      "Total Hrs Logged",
      "Avg Hrs / Day",
      "Avg Hrs / Working Day",
      "Total Missed",
      "Total Submitted",
      "Days < 4 Hrs",
      "Days < 8 Hrs",
    ];
    const headers = [
      "Sr. No.",
      "Emp ID",
      "Employee Name",
      "Department",
      ...monthlyDateColumns.map((d) => d.label),
      ...summaryHeaders,
    ];

    ws.addRow([`ProTrack - Monthly Timesheet Report | ${monthLabel} | Hours Logged Per Day`]);
    ws.mergeCells(1, 1, 1, headers.length);
    const titleCell = ws.getCell(1, 1);
    titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
    titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E79" } };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    ws.getRow(1).height = 30;

    const headerRow = ws.addRow(headers);
    headerRow.eachCell((cell, colNumber) => {
      const isSummary = colNumber > 4 + monthlyDateColumns.length;
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: isSummary ? "FF1F4E79" : "FF2F7EAD" } };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.border = {
        top: { style: "thin", color: { argb: "FF9EB6C8" } },
        bottom: { style: "thin", color: { argb: "FF9EB6C8" } },
        left: { style: "thin", color: { argb: "FF9EB6C8" } },
        right: { style: "thin", color: { argb: "FF9EB6C8" } },
      };
    });
    ws.getRow(2).height = 44;

    monthlyRows.forEach((row) => {
      const values = [
        row.sr_no,
        row.employee_number,
        row.employee_name,
        row.department,
        ...monthlyDateColumns.map((d) => monthlyCellValue(row, d.date)),
        Number(row.total_hours || 0),
        Number(row.avg_hours_per_day || 0),
        Number(row.avg_hours_per_working_day || 0),
        Number(row.total_missed || 0),
        Number(row.total_submitted || 0),
        Number(row.days_lt_4 || 0),
        Number(row.days_lt_8 || 0),
      ];
      const exRow = ws.addRow(values);
      exRow.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: "thin", color: { argb: "FFB7C9D6" } },
          bottom: { style: "thin", color: { argb: "FFB7C9D6" } },
          left: { style: "thin", color: { argb: "FFB7C9D6" } },
          right: { style: "thin", color: { argb: "FFB7C9D6" } },
        };
        cell.alignment = { horizontal: colNumber === 3 || colNumber === 4 ? "left" : "center", vertical: "middle" };
      });

      monthlyDateColumns.forEach((d, index) => {
        const col = 5 + index;
        const cell = exRow.getCell(col);
        const val = row?.daily_hours?.[d.date];
        const isLeave = Array.isArray(row?.leave_dates) && row.leave_dates.includes(d.date);
        if (!isLeave && val !== null && val !== undefined) cell.value = Number(val);
        if (isLeave) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFECEFF1" } };
          cell.font = { bold: true, color: { argb: "FF607D8B" } };
        } else if (val === null || val === undefined) {
          cell.font = { color: { argb: "FF78909C" } };
        } else if (Number(val) < 4) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFEBEE" } };
          cell.font = { bold: true, color: { argb: "FFC62828" } };
        } else if (Number(val) < 8) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF8E1" } };
          cell.font = { bold: true, color: { argb: "FFB7791F" } };
        }
      });

      for (let col = 5 + monthlyDateColumns.length; col <= headers.length; col += 1) {
        const cell = exRow.getCell(col);
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD8ECF8" } };
        cell.font = { bold: true, color: { argb: "FF1F4E79" } };
      }
    });

    ws.views = [{ state: "frozen", xSplit: 4, ySplit: 2 }];
    ws.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: headers.length } };
    ws.getColumn(1).width = 8;
    ws.getColumn(2).width = 12;
    ws.getColumn(3).width = 28;
    ws.getColumn(4).width = 16;
    monthlyDateColumns.forEach((_, index) => { ws.getColumn(5 + index).width = 9; });
    for (let col = 5 + monthlyDateColumns.length; col <= headers.length; col += 1) ws.getColumn(col).width = 14;

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `monthly_timesheet_report_${monthlyMonth}.xlsx`
    );
  };

  return (
    <div className="rv-wrap">
      {!selectedReport && (
        <>
          <div className="rv-chooser">
            <div className="rv-chooser__hero">
              <div className="rv-chooser__eyebrow">Reports</div>
              <h1 className="rv-chooser__title">Choose a Report</h1>
              <p className="rv-chooser__subtitle">Open the report you want to review for your team.</p>
            </div>

            <div className="rv-chooser__toolbar">
              <div className="rv-chooser__search">
                <IconSearch />
                <input
                  id="rv-chooser-search"
                  type="text"
                  value={chooserSearch}
                  onChange={(e) => setChooserSearch(e.target.value)}
                  placeholder="Search reports… (press &quot;/&quot; to focus)"
                />
                <kbd>⌘K</kbd>
              </div>
              <div className="rv-chooser__pills">
                {REPORT_CATEGORIES.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className={`rv-chooser__pill${chooserCategory === c.key ? " rv-chooser__pill--active" : ""}`}
                    onClick={() => setChooserCategory(c.key)}
                  >
                    {c.key === "favorite" && <IconStar filled />}
                    {c.dot && <span className="rv-chooser__pill-dot" style={{ background: RV_ACCENT_COLORS[c.dot] }} />}
                    {c.label}
                    {c.key === "all" && ` (${reportCatalogForUser.length})`}
                  </button>
                ))}
              </div>
            </div>

            {visibleReports.length === 0 ? (
              <div className="rv-chooser__empty">
                {favoritesLoaded && chooserCategory === "favorite"
                  ? "No starred reports yet — click the star on any report to add it here."
                  : "No reports match your search."}
              </div>
            ) : (
              <>
                {favoriteBannerReports.length > 0 && (
                  <section className="rv-chooser__section rv-chooser__section--favorites">
                    <div className="rv-chooser__section-head">
                      <IconStar filled />
                      <span>Favorites</span>
                      <span className="rv-chooser__section-count">{favoriteBannerReports.length} report{favoriteBannerReports.length === 1 ? "" : "s"}</span>
                    </div>
                    <div className="rv-chooser__grid">
                      {favoriteBannerReports.map((r) => (
                        <ReportChooserCard
                          key={r.key}
                          report={r}
                          isFavorite
                          onToggleFavorite={() => toggleReportFavorite(r.key)}
                          onOpen={() => setSelectedReport(r.key)}
                        />
                      ))}
                    </div>
                  </section>
                )}

                {effortGroup && effortGroup.reports.length > 0 && (
                  <ReportGroupSection
                    group={effortGroup}
                    favoriteKeys={favoriteKeys}
                    onToggleFavorite={toggleReportFavorite}
                    onOpen={setSelectedReport}
                  />
                )}

                {featuredReports.length > 0 && (
                  <section className="rv-chooser__section">
                    <div className="rv-chooser__section-head">
                      <span>Operational Core Spotlight</span>
                    </div>
                    <div className="rv-chooser__featured">
                      {featuredReports.map((r) => (
                        <ReportChooserCard
                          key={r.key}
                          report={r}
                          featured
                          isFavorite={favoriteKeys.has(r.key)}
                          onToggleFavorite={() => toggleReportFavorite(r.key)}
                          onOpen={() => setSelectedReport(r.key)}
                        />
                      ))}
                    </div>
                  </section>
                )}

                {otherGroups.map((g) => g.reports.length > 0 && (
                  <ReportGroupSection
                    key={g.key}
                    group={g}
                    favoriteKeys={favoriteKeys}
                    onToggleFavorite={toggleReportFavorite}
                    onOpen={setSelectedReport}
                  />
                ))}
              </>
            )}
          </div>
          {refPopup && (
            <RefEntriesPopup
              title={refPopup.title}
              entries={refPopup.entries}
              onClose={() => setRefPopup(null)}
            />
          )}
          {meetingPopup && (
            <MeetingEntriesPopup
              title={meetingPopup.title}
              entries={meetingPopup.entries}
              onClose={() => setMeetingPopup(null)}
            />
          )}
        </>
      )}

      {selectedReport === "monthly-timesheet" && (
        <div className="rv-panel rv-panel--monthly">
          {/* ── Header ── */}
          <header className="page-head">
            <h1>
              Monthly Timesheet{" "}
              <em>{loading ? "loading…" : fmtMonthLabel(monthlyData?.month || monthlyMonth)}</em>
            </h1>
            <div className="page-head-actions">
              <button
                className="mv2-export-btn"
                type="button"
                onClick={downloadMonthlyTimesheetExcel}
                disabled={loading || monthlyRows.length === 0}
              >
                ↓ Excel
              </button>
              <button className="rv-back-btn" type="button" onClick={() => setSelectedReport(null)}>
                ← Back
              </button>
            </div>
          </header>

          {/* ── Toolbar ── */}
          <div className="page-toolbar">
            <button className="page-tab" type="button" onClick={() => setSelectedReport(null)}>
              ← Reports
            </button>
            <span className="page-tab page-tab--active">Monthly Timesheet</span>
            <div className="page-toolbar-right">
              <label className="rv-monthly-month-label">Month</label>
              <input
                type="month"
                className="rv-monthly-month-input"
                value={monthlyMonth}
                onChange={e => setMonthlyMonth(e.target.value)}
              />
            </div>
          </div>

          {/* ── Summary stat strip ── */}
          <div className="rv-monthly-stats">
            <div className="rv-monthly-stat">
              <span className="rv-monthly-stat__label">Employees</span>
              <strong className="rv-monthly-stat__value">{loading ? "…" : monthlySummary.employees ?? 0}</strong>
            </div>
            <div className="rv-monthly-stat">
              <span className="rv-monthly-stat__label">Org Working Days</span>
              <strong className="rv-monthly-stat__value">{loading ? "…" : monthlySummary.org_working_days ?? 0}</strong>
            </div>
            <div className="rv-monthly-stat">
              <span className="rv-monthly-stat__label">Total Hrs Logged</span>
              <strong className="rv-monthly-stat__value rv-monthly-stat__value--accent">
                {loading ? "…" : Number(monthlySummary.total_hours || 0).toFixed(1)}
              </strong>
            </div>
            <div className="rv-monthly-stat">
              <span className="rv-monthly-stat__label">Total Missed</span>
              <strong className="rv-monthly-stat__value rv-monthly-stat__value--danger">
                {loading ? "…" : monthlySummary.total_missed ?? 0}
              </strong>
            </div>
          </div>

          {/* ── Table ── */}
          <div className="mv2-scroll-body">
            <div className="page-table-wrap">
              <table className="page-table rv-monthly-table">
                <thead>
                  <tr>
                    <th className="num mth-sticky-num">Sr. No.</th>
                    <th className="num mth-sticky-empid">Emp ID</th>
                    <th className="mth-sticky-name">Employee Name</th>
                    <th>Department</th>
                    {monthlyDateColumns.map((d) => (
                      <th key={d.date} className="num mth-date-col">{d.label}</th>
                    ))}
                    <th className="num mth-summary-col">Total Hrs Logged</th>
                    <th className="num mth-summary-col">Avg Hrs / Day</th>
                    <th className="num mth-summary-col">Avg Hrs / Working Day</th>
                    <th className="num mth-summary-col">Total Missed</th>
                    <th className="num mth-summary-col">Total Submitted</th>
                    <th className="num mth-summary-col">Days &lt; 4 Hrs</th>
                    <th className="num mth-summary-col">Days &lt; 8 Hrs</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={monthlyDateColumns.length + 11} style={{ textAlign: "center", padding: "32px 0", color: "var(--mv2-text-muted, #5C6069)" }}>
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!loading && monthlyRows.length === 0 && (
                    <tr>
                      <td colSpan={monthlyDateColumns.length + 11} style={{ textAlign: "center", padding: "40px 0", color: "var(--mv2-text-muted, #5C6069)" }}>
                        No employees found for {fmtMonthLabel(monthlyMonth)}
                      </td>
                    </tr>
                  )}
                  {!loading && monthlyRows.map((row) => (
                    <tr key={row.user_id}>
                      <td className="num mth-sticky-num" style={{ color: "var(--mv2-text-faint, #8B8F99)", fontSize: 12 }}>{row.sr_no}</td>
                      <td className="num mth-sticky-empid" style={{ fontFamily: "var(--mv2-mono, monospace)", fontSize: 12 }}>{row.employee_number || "—"}</td>
                      <td className="mth-sticky-name" style={{ fontWeight: 500 }}>{row.employee_name || "—"}</td>
                      <td>{row.department || "Unassigned"}</td>
                      {monthlyDateColumns.map((d) => (
                        <td
                          key={`${row.user_id}-${d.date}`}
                          className="num mth-date-col"
                          style={monthlyCellStyle(row, d.date)}
                        >
                          {monthlyCellValue(row, d.date)}
                        </td>
                      ))}
                      <td className="num mth-summary-col" style={{ fontWeight: 600 }}>{Number(row.total_hours || 0).toFixed(1)}</td>
                      <td className="num mth-summary-col">{Number(row.avg_hours_per_day || 0).toFixed(1)}</td>
                      <td className="num mth-summary-col">{Number(row.avg_hours_per_working_day || 0).toFixed(1)}</td>
                      <td className="num mth-summary-col" style={{ color: "var(--mv2-danger, #B03A2E)", fontWeight: 600 }}>{row.total_missed || 0}</td>
                      <td className="num mth-summary-col" style={{ color: "var(--mv2-success, #2F7D4F)", fontWeight: 600 }}>{row.total_submitted || 0}</td>
                      <td className="num mth-summary-col" style={{ color: "var(--mv2-danger, #B03A2E)" }}>{row.days_lt_4 || 0}</td>
                      <td className="num mth-summary-col" style={{ color: "var(--mv2-warn, #C8842E)" }}>{row.days_lt_8 || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {selectedReport === "nc" && (
        <div className="rv-panel">
          <div className="rv-panel__header rv-panel__header--with-toolbar">
            <div className="rv-panel__titlebar">
              <button className="rv-back-btn" type="button" onClick={() => setSelectedReport(null)}>← Back</button>
              <span className="rv-panel__title">Non-Compliance Report</span>
            </div>
            <div className="rv-toolbar rv-toolbar--inline">
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">From</label>
                <input
                  type="date"
                  className="rv-toolbar__date"
                  value={dateFrom}
                  max={dateTo}
                  onChange={e => setDateFrom(e.target.value)}
                />
              </div>
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">To</label>
                <input
                  type="date"
                  className="rv-toolbar__date"
                  value={dateTo}
                  min={dateFrom}
                  onChange={e => setDateTo(e.target.value)}
                />
              </div>
              <button
                className="rv-download-btn"
                type="button"
                onClick={downloadNcExcel}
                disabled={loading || filteredNcRows.length === 0}
              >
                ↓ Download Excel
              </button>
              <button
                className={`toolbar__add-btn mv-filter-btn${ncFilterOpen ? " mv-filter-btn--active" : ""}`}
                type="button"
                onClick={() => setNcFilterOpen((v) => !v)}
              >
                ⚙ Filters{activeNcFilterCount > 0 && <span className="mv-filter-badge">{activeNcFilterCount}</span>}
              </button>
            </div>
          </div>

          {ncFilterOpen && (
            <div className="mv-filter-panel rv-filter-flyout">
              <div className="mv-filter-panel__sections">
                <div className="mv-filter-section">
                  <div className="mv-filter-section__fields">
                    <div className="mv-filter-field">
                      <label className="mv-filter-label">Avg Time entry</label>
                      <div className="rv-filter-inline">
                        <select
                          className="mv-filter-select"
                          value={ncFilters.avg_minutes.op}
                          onChange={(e) => updateNcFilter("avg_minutes", { op: e.target.value })}
                        >
                          {NC_FILTER_OPS.map((op) => <option key={op} value={op}>{op}</option>)}
                        </select>
                        <input
                          className="mv-filter-input"
                          type="number"
                          min="0"
                          placeholder="Minutes"
                          value={ncFilters.avg_minutes.value}
                          onChange={(e) => updateNcFilter("avg_minutes", { value: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="mv-filter-field">
                      <label className="mv-filter-label">Overdue Tasks</label>
                      <div className="rv-filter-inline">
                        <select
                          className="mv-filter-select"
                          value={ncFilters.overdue_tasks.op}
                          onChange={(e) => updateNcFilter("overdue_tasks", { op: e.target.value })}
                        >
                          {NC_FILTER_OPS.map((op) => <option key={op} value={op}>{op}</option>)}
                        </select>
                        <input
                          className="mv-filter-input"
                          type="number"
                          min="0"
                          placeholder="Count"
                          value={ncFilters.overdue_tasks.value}
                          onChange={(e) => updateNcFilter("overdue_tasks", { value: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="mv-filter-field">
                      <label className="mv-filter-label">Allocation Hours</label>
                      <div className="rv-filter-inline">
                        <select
                          className="mv-filter-select"
                          value={ncFilters.allocation_hours.op}
                          onChange={(e) => updateNcFilter("allocation_hours", { op: e.target.value })}
                        >
                          {NC_FILTER_OPS.map((op) => <option key={op} value={op}>{op}</option>)}
                        </select>
                        <input
                          className="mv-filter-input"
                          type="number"
                          min="0"
                          step="0.1"
                          placeholder="Hours"
                          value={ncFilters.allocation_hours.value}
                          onChange={(e) => updateNcFilter("allocation_hours", { value: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mv-filter-panel__footer">
                <span className="mv-filter-summary">{activeNcFilterCount} filter{activeNcFilterCount !== 1 ? "s" : ""} active · {filteredNcRows.length} rows</span>
                <button className="mv-filter-clear" type="button" onClick={clearNcFilters} disabled={activeNcFilterCount === 0}>Clear All</button>
              </div>
            </div>
          )}

          <div className="rv-table-wrap">
            <table className="rv-table">
              <thead>
                <tr>
                  <th className="rv-th rv-th--num">S.No</th>
                  <th className="rv-th">User</th>
                  <th className="rv-th rv-th--num">Avg Time entry</th>
                  <th className="rv-th rv-th--num">Overdue Tasks</th>
                  <th className="rv-th rv-th--num">Allocation Hours</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} className="rv-state">Loading…</td></tr>
                )}
                {!loading && filteredNcRows.length === 0 && (
                  <tr><td colSpan={5} className="rv-state">No non-compliant users found.</td></tr>
                )}
                {!loading && filteredNcRows.map((row, i) => (
                  <tr key={row.user_id || row.user_name || i} className="rv-row">
                    <td className="rv-td rv-td--num">{i + 1}</td>
                    <td className="rv-td">{row.user_display_name || row.user_name || "—"}</td>
                    <td
                      className="rv-td rv-td--num"
                      style={(Number(row.avg_minutes) || 0) < 480 ? { background: "#fff3e0", fontWeight: 700, color: "#b45309" } : {}}
                    >
                      {fmtTime(Number(row.avg_minutes) || 0)}
                    </td>
                    <td
                      className="rv-td rv-td--num"
                      style={(Number(row.overdue_tasks) || 0) > 0 ? { background: "#ffebee", fontWeight: 700, color: "#b71c1c" } : {}}
                    >
                      {Number(row.overdue_tasks) || 0}
                    </td>
                    <td
                      className="rv-td rv-td--num"
                      style={(Number(row.allocation_hours) || 0) < 40 ? { background: "#f3e8ff", fontWeight: 700, color: "#7c3aed" } : {}}
                    >
                      {fmtHoursDecimal(row.allocation_hours)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedReport === "efforts-customer" && effortDrillPath.length < 4 && (
        <div className="rv-panel rv-panel--efc rv-efc-scope">
          <header className="page-head">
            <h1>Efforts by Customer</h1>
            <div className="page-head-actions">
              {efcScoped && <span className="rv-scope-badge" title="Showing only data for projects you're allocated to">Your allocated projects</span>}
              {/* Table / bar-chart display toggle */}
              <div className="mv2-view-toggle">
                <button
                  className={`mv2-view-btn${effortLevelDisplayMode === "table" ? " mv2-view-btn--active" : ""}`}
                  onClick={() => setEffortLevelDisplayMode("table")}
                  title="Table view"
                >
                  <IconTable />
                </button>
                <button
                  className={`mv2-view-btn${effortLevelDisplayMode === "chart" ? " mv2-view-btn--active" : ""}`}
                  onClick={() => setEffortLevelDisplayMode("chart")}
                  title="Bar chart view"
                >
                  <IconBarChart />
                </button>
              </div>
              <button
                className="rv-download-btn"
                type="button"
                onClick={downloadCustomerEffortsExcel}
                disabled={effortLevelLoading || effortLevelData.rows.length === 0}
              >
                <IconDownload />
                Export
              </button>
              <button className="mv2-new-task-btn v2-btn-primary" type="button" onClick={() => setSelectedReport(null)}>
                ← Back
              </button>
            </div>
          </header>
          <div className="page-toolbar">
            {effortDrillPath.length > 0 && (
              <div className="efc-chart-breadcrumb">
                <button type="button" className="efc-chart-back" onClick={() => drillToDepth(0)}>
                  ← All Customers
                </button>
                {effortDrillPath.map((p, idx) => (
                  <Fragment key={idx}>
                    <span className="efc-chart-breadcrumb-sep">/</span>
                    {idx === effortDrillPath.length - 1 ? (
                      <span className="efc-chart-breadcrumb-current">{p.name}</span>
                    ) : (
                      <button type="button" className="efc-chart-back" onClick={() => drillToDepth(idx + 1)}>
                        {p.name}
                      </button>
                    )}
                  </Fragment>
                ))}
              </div>
            )}
            <div className="page-toolbar-right">
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">From</label>
                <input
                  type="date"
                  className="rv-toolbar__date"
                  value={efcDateFrom}
                  max={efcDateTo}
                  onChange={e => setEfcDateFrom(e.target.value)}
                />
              </div>
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">To</label>
                <input
                  type="date"
                  className="rv-toolbar__date"
                  value={efcDateTo}
                  min={efcDateFrom}
                  onChange={e => setEfcDateTo(e.target.value)}
                />
              </div>
            </div>
          </div>

          {effortLevelDisplayMode === "chart" ? (
            <div className="efc-chart-layout">
              <div className="efc-chart-panel efc-chart-panel--main">
                <div className="efc-chart-scroll">
                  {effortLevelLoading ? (
                    <div className="rv-state efc-chart-empty">Loading…</div>
                  ) : (
                    <EfcBarChart
                      rows={effortLevelChartRows}
                      onBarClick={(row) => {
                        if (row) drillInto(EFFORT_LEVEL_NAMES[effortDrillPath.length], row.id, row.label);
                      }}
                      emptyLabel={`No entries found for ${efcDateLabel}`}
                    />
                  )}
                </div>
              </div>
              <div className="efc-chart-sidebar">
                <EfcMiniDonut
                  title={efcDonutA.title}
                  unitLabel={efcDonutA.unit}
                  rows={efcDonutRows(efcDonutA)}
                  emptyLabel={efcDonutA.loading ? "Loading..." : "No effort found"}
                />
                <EfcMiniDonut
                  title={efcDonutB.title}
                  unitLabel={efcDonutB.unit}
                  rows={efcDonutRows(efcDonutB)}
                  emptyLabel={efcDonutB.loading ? "Loading..." : "No effort found"}
                />
              </div>
            </div>
          ) : (
          <div className="rv-table-wrap rv-detail-wrap">
            <div className="rv-detail-scroll">
              <table className="rv-table rv-detail-table">
                <thead>
                  <tr>
                    <th className="rv-th rv-th--num rv-th--sticky">S.No</th>
                    <th className="rv-th rv-th--sticky-user" onClick={() => toggleEffortLevelSort("label")} style={{ cursor: "pointer" }}>
                      {effortLevelColumnLabel} {effortLevelSortKey === "label" ? (effortLevelSortDir === "asc" ? "↑" : "↓") : ""}
                    </th>
                    <th className="rv-th rv-th--num rv-th--sticky-total" onClick={() => toggleEffortLevelSort("total")} style={{ cursor: "pointer" }}>
                      Total {effortLevelSortKey === "total" ? (effortLevelSortDir === "asc" ? "↑" : "↓") : ""}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {effortLevelLoading && (
                    <tr><td colSpan={3} className="rv-state">Loading…</td></tr>
                  )}
                  {!effortLevelLoading && effortLevelData.rows.length === 0 && (
                    <tr><td colSpan={3} className="rv-state">No entries found for {efcDateLabel}</td></tr>
                  )}
                  {!effortLevelLoading && sortedEffortLevelRows.map((row, i) => (
                    <tr key={row.key || `row-${i}`} className="rv-row">
                      <td className="rv-td rv-td--num rv-td--sticky">{i + 1}</td>
                      <td className="rv-td rv-td--sticky-user">{row.label || "—"}</td>
                      <td className="rv-td rv-td--num rv-td--sticky-total" style={{ fontWeight: 700 }}>
                        {row.total_minutes > 0 ? (
                          <button
                            type="button"
                            className="efd-cell-btn"
                            onClick={() => drillInto(EFFORT_LEVEL_NAMES[effortDrillPath.length], row.id, row.label)}
                          >
                            {fmtTime(row.total_minutes)}
                          </button>
                        ) : fmtTime(row.total_minutes)}
                      </td>
                    </tr>
                  ))}
                  {!effortLevelLoading && effortLevelData.rows.length > 0 && (
                    <tr className="rv-row">
                      <td className="rv-td rv-td--sticky" />
                      <td className="rv-td rv-td--sticky-user" style={{ fontWeight: 700 }}>Total</td>
                      <td className="rv-td rv-td--num rv-td--sticky-total" style={{ fontWeight: 700 }}>
                        {fmtTime(Number(effortLevelData.grand_total_minutes) || 0)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </div>
      )}

      {selectedReport === "efforts-customer" && effortDrillPath.length === 4 && (
        <div className="rv-panel rv-efc-scope">
          <header className="page-head">
            <h1>Efforts by Customer</h1>
            <div className="page-head-actions">
              {efcScoped && <span className="rv-scope-badge" title="Showing only data for projects you're allocated to">Your allocated projects</span>}
              <div className="mv2-view-toggle">
                <button
                  className={`mv2-view-btn${effortDrilldownDisplayMode === "table" ? " mv2-view-btn--active" : ""}`}
                  onClick={() => setEffortDrilldownDisplayMode("table")}
                  title="Table view"
                >
                  <IconTable />
                </button>
                <button
                  className={`mv2-view-btn${effortDrilldownDisplayMode === "chart" ? " mv2-view-btn--active" : ""}`}
                  onClick={() => setEffortDrilldownDisplayMode("chart")}
                  title="Bar chart view"
                >
                  <IconBarChart />
                </button>
              </div>
              <button className="mv2-new-task-btn v2-btn-primary" type="button" onClick={() => setSelectedReport(null)}>
                ← Back
              </button>
            </div>
          </header>
          <div className="page-toolbar">
            <div className="efc-chart-breadcrumb">
              <button type="button" className="efc-chart-back" onClick={() => drillToDepth(0)}>
                ← All Customers
              </button>
              {effortDrillPath.map((p, idx) => (
                <Fragment key={idx}>
                  <span className="efc-chart-breadcrumb-sep">/</span>
                  <button type="button" className="efc-chart-back" onClick={() => drillToDepth(idx + 1)}>
                    {p.name}
                  </button>
                </Fragment>
              ))}
              <span className="efc-chart-breadcrumb-sep">/</span>
              <span className="efc-chart-breadcrumb-current">Resources</span>
            </div>
            <div className="page-toolbar-right">
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">From</label>
                <input
                  type="date"
                  className="rv-toolbar__date"
                  value={efcDateFrom}
                  max={efcDateTo}
                  onChange={e => setEfcDateFrom(e.target.value)}
                />
              </div>
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">To</label>
                <input
                  type="date"
                  className="rv-toolbar__date"
                  value={efcDateTo}
                  min={efcDateFrom}
                  onChange={e => setEfcDateTo(e.target.value)}
                />
              </div>
            </div>
          </div>

          {effortDrilldownDisplayMode === "chart" ? (
            <div className="efc-chart-layout">
              <div className="efc-chart-panel efc-chart-panel--main">
                <div className="efc-chart-scroll">
                  {effortDrilldownLoading ? (
                    <div className="rv-state efc-chart-empty">Loading…</div>
                  ) : (
                    <EfcBarChart
                      rows={effortDrilldownChartRows}
                      onBarClick={(row) => {
                        if (row && row.value > 0) toggleEffortDrilldownExpand(row.user_id, null);
                      }}
                      isRowExpanded={(r) => !!effortDrilldownExpanded[`${r.user_id}:total`]}
                      renderExpanded={(r) => {
                        const exp = effortDrilldownExpanded[`${r.user_id}:total`];
                        if (!exp) return null;
                        if (exp.loading) return "Loading…";
                        if (exp.rows.length === 0) return "No entries found.";
                        return (
                          <table className="efd-detail-table">
                            <thead>
                              <tr><th>Date</th><th className="efd-detail-table__type">Type</th><th>Reference</th><th>Hours</th></tr>
                            </thead>
                            <tbody>
                              {exp.rows.map((row) => (
                                <tr key={row.entry_id}>
                                  <td>{row.report_date}</td>
                                  <td className="efd-detail-table__type">{renderEfdType(row)}</td>
                                  <td>{renderEfdReference(row)}</td>
                                  <td>{fmtTime((Number(row.hours_spent) || 0) * 60 + (Number(row.minutes_spent) || 0))}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        );
                      }}
                      emptyLabel={`No entries found for ${efcDateLabel}`}
                    />
                  )}
                </div>
              </div>
              <div className="efc-chart-sidebar">
                <EfcMiniDonut
                  title={efcDonutA.title}
                  unitLabel={efcDonutA.unit}
                  rows={efcDonutRows(efcDonutA)}
                  emptyLabel={efcDonutA.loading ? "Loading..." : "No effort found"}
                />
                <EfcMiniDonut
                  title={efcDonutB.title}
                  unitLabel={efcDonutB.unit}
                  rows={efcDonutRows(efcDonutB)}
                  emptyLabel={efcDonutB.loading ? "Loading..." : "No effort found"}
                />
              </div>
            </div>
          ) : (
          <div className="rv-table-wrap rv-detail-wrap">
            <div className="rv-detail-scroll">
              <table className="rv-table rv-detail-table">
                <thead>
                  <tr>
                    <th className="rv-th rv-th--num rv-th--sticky">S.No</th>
                    <th className="rv-th rv-th--sticky-user">User Name</th>
                    {EFD_CATEGORY_COLUMNS.map((c) => (
                      <th key={c.key} className="rv-th rv-th--num">{c.label}</th>
                    ))}
                    <th className="rv-th rv-th--num">Total hours</th>
                  </tr>
                </thead>
                <tbody>
                  {effortDrilldownLoading && (
                    <tr><td colSpan={EFD_CATEGORY_COLUMNS.length + 3} className="rv-state">Loading…</td></tr>
                  )}
                  {!effortDrilldownLoading && effortDrilldownData.rows.length === 0 && (
                    <tr><td colSpan={EFD_CATEGORY_COLUMNS.length + 3} className="rv-state">No entries found for {efcDateLabel}</td></tr>
                  )}
                  {!effortDrilldownLoading && effortDrilldownData.rows.map((row, i) => {
                    const totalKey = `${row.user_id}:total`;
                    const totalExpanded = effortDrilldownExpanded[totalKey];
                    return (
                      <Fragment key={row.user_id || i}>
                        <tr className="rv-row">
                          <td className="rv-td rv-td--num rv-td--sticky">{i + 1}</td>
                          <td className="rv-td rv-td--sticky-user">{row.user_name || "—"}</td>
                          {EFD_CATEGORY_COLUMNS.map((c) => {
                            const mins = Number(row[c.key]) || 0;
                            const key = `${row.user_id}:${c.entryCategory}`;
                            const isOpen = !!effortDrilldownExpanded[key];
                            return (
                              <td key={c.key} className="rv-td rv-td--num">
                                {mins > 0 ? (
                                  <button
                                    type="button"
                                    className={`efd-cell-btn${isOpen ? " efd-cell-btn--active" : ""}`}
                                    onClick={() => toggleEffortDrilldownExpand(row.user_id, c.entryCategory)}
                                  >
                                    {fmtTime(mins)}
                                  </button>
                                ) : fmtTime(mins)}
                              </td>
                            );
                          })}
                          <td className="rv-td rv-td--num" style={{ fontWeight: 700 }}>
                            {Number(row.total_minutes) > 0 ? (
                              <button
                                type="button"
                                className={`efd-cell-btn${totalExpanded ? " efd-cell-btn--active" : ""}`}
                                onClick={() => toggleEffortDrilldownExpand(row.user_id, null)}
                              >
                                {fmtTime(Number(row.total_minutes) || 0)}
                              </button>
                            ) : fmtTime(Number(row.total_minutes) || 0)}
                          </td>
                        </tr>
                        {EFD_CATEGORY_COLUMNS.map((c) => {
                          const key = `${row.user_id}:${c.entryCategory}`;
                          const exp = effortDrilldownExpanded[key];
                          if (!exp) return null;
                          return (
                            <tr key={key} className="efd-expand-row">
                              <td className="rv-td rv-td--sticky" />
                              <td className="rv-td" colSpan={EFD_CATEGORY_COLUMNS.length + 2}>
                                {exp.loading ? "Loading…" : (
                                  exp.rows.length === 0 ? "No entries found." : (
                                    <table className="efd-detail-table">
                                      <thead>
                                        <tr><th>Date</th><th className="efd-detail-table__type">Type</th><th>Reference</th><th>Hours</th></tr>
                                      </thead>
                                      <tbody>
                                        {exp.rows.map((r) => (
                                          <tr key={r.entry_id}>
                                            <td>{r.report_date}</td>
                                            <td className="efd-detail-table__type">{renderEfdType(r)}</td>
                                            <td>{renderEfdReference(r)}</td>
                                            <td>{fmtTime((Number(r.hours_spent) || 0) * 60 + (Number(r.minutes_spent) || 0))}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {totalExpanded && (
                          <tr className="efd-expand-row">
                            <td className="rv-td rv-td--sticky" />
                            <td className="rv-td" colSpan={EFD_CATEGORY_COLUMNS.length + 2}>
                              {totalExpanded.loading ? "Loading…" : (
                                totalExpanded.rows.length === 0 ? "No entries found." : (
                                  <table className="efd-detail-table">
                                    <thead>
                                      <tr><th>Date</th><th className="efd-detail-table__cat">Category</th><th className="efd-detail-table__type">Type</th><th>Reference</th><th>Hours</th></tr>
                                    </thead>
                                    <tbody>
                                      {totalExpanded.rows.map((r) => (
                                        <tr key={r.entry_id}>
                                          <td>{r.report_date}</td>
                                          <td className="efd-detail-table__cat">{r.category_name}</td>
                                          <td className="efd-detail-table__type">{renderEfdType(r)}</td>
                                          <td>{renderEfdReference(r)}</td>
                                          <td>{fmtTime((Number(r.hours_spent) || 0) * 60 + (Number(r.minutes_spent) || 0))}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {!effortDrilldownLoading && effortDrilldownData.rows.length > 0 && (
                    <tr className="rv-row">
                      <td className="rv-td rv-td--sticky" />
                      <td className="rv-td rv-td--sticky-user" style={{ fontWeight: 700 }}>Total</td>
                      {EFD_CATEGORY_COLUMNS.map((c) => (
                        <td key={`total-${c.key}`} className="rv-td rv-td--num" style={{ fontWeight: 700 }}>
                          {fmtTime(Number(effortDrilldownData.totals?.[c.key]) || 0)}
                        </td>
                      ))}
                      <td className="rv-td rv-td--num" style={{ fontWeight: 700 }}>
                        {fmtTime(Number(effortDrilldownData.totals?.total_minutes) || 0)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </div>
      )}

      {selectedReport === "efforts-team" && eftDrillPath.length < eftFinalDepth && (
        <div className="rv-panel rv-panel--efc rv-panel--eft rv-efc-scope rv-eft-scope">
          <header className="page-head">
            <h1>Efforts per Team</h1>
            <div className="page-head-actions">
              {/* Quick (Team -> Resource) vs Full Drill (Team -> Customer -> Project -> Epic -> Resource) */}
              {eftDrillPath.length === 0 && (
                <div className="mv2-view-toggle rv-eft-mode-toggle">
                  <button
                    className={`mv2-view-btn rv-eft-mode-btn${eftMode === "simple" ? " mv2-view-btn--active" : ""}`}
                    onClick={() => setEftModeAndReset("simple")}
                    title="Quick drill: Team → Resource"
                  >
                    Quick
                  </button>
                  <button
                    className={`mv2-view-btn rv-eft-mode-btn${eftMode === "full" ? " mv2-view-btn--active" : ""}`}
                    onClick={() => setEftModeAndReset("full")}
                    title="Full drill: Team → Customer → Project → Epic → Resource"
                  >
                    Full Drill
                  </button>
                </div>
              )}
              {/* Table / bar-chart display toggle */}
              <div className="mv2-view-toggle">
                <button
                  className={`mv2-view-btn${eftLevelDisplayMode === "table" ? " mv2-view-btn--active" : ""}`}
                  onClick={() => setEftLevelDisplayMode("table")}
                  title="Table view"
                >
                  <IconTable />
                </button>
                <button
                  className={`mv2-view-btn${eftLevelDisplayMode === "chart" ? " mv2-view-btn--active" : ""}`}
                  onClick={() => setEftLevelDisplayMode("chart")}
                  title="Bar chart view"
                >
                  <IconBarChart />
                </button>
              </div>
              <button
                className="rv-download-btn"
                type="button"
                onClick={downloadTeamEffortsExcel}
                disabled={eftLevelLoading || eftLevelData.rows.length === 0}
              >
                <IconDownload />
                Export
              </button>
              <button className="mv2-new-task-btn v2-btn-primary" type="button" onClick={() => setSelectedReport(null)}>
                ← Back
              </button>
            </div>
          </header>
          <div className="page-toolbar">
            {eftDrillPath.length > 0 && (
              <div className="efc-chart-breadcrumb">
                <button type="button" className="efc-chart-back" onClick={() => eftDrillToDepth(0)}>
                  ← All Teams
                </button>
                {eftDrillPath.map((p, idx) => (
                  <Fragment key={idx}>
                    <span className="efc-chart-breadcrumb-sep">/</span>
                    {idx === eftDrillPath.length - 1 ? (
                      <span className="efc-chart-breadcrumb-current">{p.name}</span>
                    ) : (
                      <button type="button" className="efc-chart-back" onClick={() => eftDrillToDepth(idx + 1)}>
                        {p.name}
                      </button>
                    )}
                  </Fragment>
                ))}
              </div>
            )}
            <div className="page-toolbar-right">
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">From</label>
                <input
                  type="date"
                  className="rv-toolbar__date"
                  value={eftDateFrom}
                  max={eftDateTo}
                  onChange={e => setEftDateFrom(e.target.value)}
                />
              </div>
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">To</label>
                <input
                  type="date"
                  className="rv-toolbar__date"
                  value={eftDateTo}
                  min={eftDateFrom}
                  onChange={e => setEftDateTo(e.target.value)}
                />
              </div>
            </div>
          </div>

          {eftLevelDisplayMode === "chart" ? (
            <div className="efc-chart-layout">
              <div className="efc-chart-panel efc-chart-panel--main">
                <div className="efc-chart-scroll">
                  {eftLevelLoading ? (
                    <div className="rv-state efc-chart-empty">Loading…</div>
                  ) : (
                    <EfcBarChart
                      rows={eftLevelChartRows}
                      onBarClick={(row) => {
                        if (row) eftDrillInto(EFT_LEVEL_NAMES[eftDrillPath.length], row.id, row.label);
                      }}
                      emptyLabel={`No entries found for ${eftDateLabel}`}
                    />
                  )}
                </div>
              </div>
              <div className="efc-chart-sidebar">
                <EfcMiniDonut
                  title={eftDonutA.title}
                  unitLabel={eftDonutA.unit}
                  rows={efcDonutRows(eftDonutA)}
                  emptyLabel={eftDonutA.loading ? "Loading..." : "No effort found"}
                />
                <EfcMiniDonut
                  title={eftDonutB.title}
                  unitLabel={eftDonutB.unit}
                  rows={efcDonutRows(eftDonutB)}
                  emptyLabel={eftDonutB.loading ? "Loading..." : "No effort found"}
                />
              </div>
            </div>
          ) : (
          <div className="rv-table-wrap rv-detail-wrap">
            <div className="rv-detail-scroll">
              <table className="rv-table rv-detail-table">
                <thead>
                  <tr>
                    <th className="rv-th rv-th--num rv-th--sticky">S.No</th>
                    <th className="rv-th rv-th--sticky-user" onClick={() => toggleEftLevelSort("label")} style={{ cursor: "pointer" }}>
                      {eftLevelColumnLabel} {eftLevelSortKey === "label" ? (eftLevelSortDir === "asc" ? "↑" : "↓") : ""}
                    </th>
                    <th className="rv-th rv-th--num rv-th--sticky-total" onClick={() => toggleEftLevelSort("total")} style={{ cursor: "pointer" }}>
                      Total {eftLevelSortKey === "total" ? (eftLevelSortDir === "asc" ? "↑" : "↓") : ""}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {eftLevelLoading && (
                    <tr><td colSpan={3} className="rv-state">Loading…</td></tr>
                  )}
                  {!eftLevelLoading && eftLevelData.rows.length === 0 && (
                    <tr><td colSpan={3} className="rv-state">No entries found for {eftDateLabel}</td></tr>
                  )}
                  {!eftLevelLoading && sortedEftLevelRows.map((row, i) => (
                    <tr key={row.key || `row-${i}`} className="rv-row">
                      <td className="rv-td rv-td--num rv-td--sticky">{i + 1}</td>
                      <td className="rv-td rv-td--sticky-user">{row.label || "—"}</td>
                      <td className="rv-td rv-td--num rv-td--sticky-total" style={{ fontWeight: 700 }}>
                        {row.total_minutes > 0 ? (
                          <button
                            type="button"
                            className="efd-cell-btn"
                            onClick={() => eftDrillInto(EFT_LEVEL_NAMES[eftDrillPath.length], row.id, row.label)}
                          >
                            {fmtTime(row.total_minutes)}
                          </button>
                        ) : fmtTime(row.total_minutes)}
                      </td>
                    </tr>
                  ))}
                  {!eftLevelLoading && eftLevelData.rows.length > 0 && (
                    <tr className="rv-row">
                      <td className="rv-td rv-td--sticky" />
                      <td className="rv-td rv-td--sticky-user" style={{ fontWeight: 700 }}>Total</td>
                      <td className="rv-td rv-td--num rv-td--sticky-total" style={{ fontWeight: 700 }}>
                        {fmtTime(Number(eftLevelData.grand_total_minutes) || 0)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </div>
      )}

      {selectedReport === "efforts-team" && eftDrillPath.length === eftFinalDepth && (
        <div className="rv-panel rv-efc-scope rv-eft-scope">
          <header className="page-head">
            <h1>Efforts per Team</h1>
            <div className="page-head-actions">
              <div className="mv2-view-toggle">
                <button
                  className={`mv2-view-btn${eftEntriesDisplayMode === "table" ? " mv2-view-btn--active" : ""}`}
                  onClick={() => setEftEntriesDisplayMode("table")}
                  title="Table view"
                >
                  <IconTable />
                </button>
                <button
                  className={`mv2-view-btn${eftEntriesDisplayMode === "chart" ? " mv2-view-btn--active" : ""}`}
                  onClick={() => setEftEntriesDisplayMode("chart")}
                  title="Bar chart view"
                >
                  <IconBarChart />
                </button>
              </div>
              <button className="mv2-new-task-btn v2-btn-primary" type="button" onClick={() => setSelectedReport(null)}>
                ← Back
              </button>
            </div>
          </header>
          <div className="page-toolbar">
            <div className="efc-chart-breadcrumb">
              <button type="button" className="efc-chart-back" onClick={() => eftDrillToDepth(0)}>
                ← All Teams
              </button>
              {eftDrillPath.map((p, idx) => (
                <Fragment key={idx}>
                  <span className="efc-chart-breadcrumb-sep">/</span>
                  <button type="button" className="efc-chart-back" onClick={() => eftDrillToDepth(idx + 1)}>
                    {p.name}
                  </button>
                </Fragment>
              ))}
              <span className="efc-chart-breadcrumb-sep">/</span>
              <span className="efc-chart-breadcrumb-current">Resources</span>
            </div>
            <div className="page-toolbar-right">
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">From</label>
                <input
                  type="date"
                  className="rv-toolbar__date"
                  value={eftDateFrom}
                  max={eftDateTo}
                  onChange={e => setEftDateFrom(e.target.value)}
                />
              </div>
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">To</label>
                <input
                  type="date"
                  className="rv-toolbar__date"
                  value={eftDateTo}
                  min={eftDateFrom}
                  onChange={e => setEftDateTo(e.target.value)}
                />
              </div>
            </div>
          </div>

          {eftEntriesDisplayMode === "chart" ? (
            <div className="efc-chart-layout">
              <div className="efc-chart-panel efc-chart-panel--main">
                <div className="efc-chart-scroll">
                  {eftEntriesLoading ? (
                    <div className="rv-state efc-chart-empty">Loading…</div>
                  ) : (
                    <EfcBarChart
                      rows={eftEntriesChartRows}
                      onBarClick={(row) => {
                        if (row && row.value > 0) toggleEftEntriesExpand(row.user_id, null);
                      }}
                      isRowExpanded={(r) => !!eftEntriesExpanded[`${r.user_id}:total`]}
                      renderExpanded={(r) => {
                        const exp = eftEntriesExpanded[`${r.user_id}:total`];
                        if (!exp) return null;
                        if (exp.loading) return "Loading…";
                        if (exp.rows.length === 0) return "No entries found.";
                        return (
                          <table className="efd-detail-table">
                            <thead>
                              <tr><th>Date</th><th className="efd-detail-table__type">Type</th><th>Reference</th><th>Hours</th></tr>
                            </thead>
                            <tbody>
                              {exp.rows.map((row) => (
                                <tr key={row.entry_id}>
                                  <td>{row.report_date}</td>
                                  <td className="efd-detail-table__type">{renderEfdType(row)}</td>
                                  <td>{renderEfdReference(row)}</td>
                                  <td>{fmtTime((Number(row.hours_spent) || 0) * 60 + (Number(row.minutes_spent) || 0))}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        );
                      }}
                      emptyLabel={`No entries found for ${eftDateLabel}`}
                    />
                  )}
                </div>
              </div>
              <div className="efc-chart-sidebar">
                <EfcMiniDonut
                  title={eftDonutA.title}
                  unitLabel={eftDonutA.unit}
                  rows={efcDonutRows(eftDonutA)}
                  emptyLabel={eftDonutA.loading ? "Loading..." : "No effort found"}
                />
                <EfcMiniDonut
                  title={eftDonutB.title}
                  unitLabel={eftDonutB.unit}
                  rows={efcDonutRows(eftDonutB)}
                  emptyLabel={eftDonutB.loading ? "Loading..." : "No effort found"}
                />
              </div>
            </div>
          ) : (
          <div className="rv-table-wrap rv-detail-wrap">
            <div className="rv-detail-scroll">
              <table className="rv-table rv-detail-table">
                <thead>
                  <tr>
                    <th className="rv-th rv-th--num rv-th--sticky">S.No</th>
                    <th className="rv-th rv-th--sticky-user">User Name</th>
                    {EFD_CATEGORY_COLUMNS.map((c) => (
                      <th key={c.key} className="rv-th rv-th--num">{c.label}</th>
                    ))}
                    <th className="rv-th rv-th--num">Total hours</th>
                  </tr>
                </thead>
                <tbody>
                  {eftEntriesLoading && (
                    <tr><td colSpan={EFD_CATEGORY_COLUMNS.length + 3} className="rv-state">Loading…</td></tr>
                  )}
                  {!eftEntriesLoading && eftEntriesData.rows.length === 0 && (
                    <tr><td colSpan={EFD_CATEGORY_COLUMNS.length + 3} className="rv-state">No entries found for {eftDateLabel}</td></tr>
                  )}
                  {!eftEntriesLoading && eftEntriesData.rows.map((row, i) => {
                    const totalKey = `${row.user_id}:total`;
                    const totalExpanded = eftEntriesExpanded[totalKey];
                    return (
                      <Fragment key={row.user_id || i}>
                        <tr className="rv-row">
                          <td className="rv-td rv-td--num rv-td--sticky">{i + 1}</td>
                          <td className="rv-td rv-td--sticky-user">{row.user_name || "—"}</td>
                          {EFD_CATEGORY_COLUMNS.map((c) => {
                            const mins = Number(row[c.key]) || 0;
                            const key = `${row.user_id}:${c.entryCategory}`;
                            const isOpen = !!eftEntriesExpanded[key];
                            return (
                              <td key={c.key} className="rv-td rv-td--num">
                                {mins > 0 ? (
                                  <button
                                    type="button"
                                    className={`efd-cell-btn${isOpen ? " efd-cell-btn--active" : ""}`}
                                    onClick={() => toggleEftEntriesExpand(row.user_id, c.entryCategory)}
                                  >
                                    {fmtTime(mins)}
                                  </button>
                                ) : fmtTime(mins)}
                              </td>
                            );
                          })}
                          <td className="rv-td rv-td--num" style={{ fontWeight: 700 }}>
                            {Number(row.total_minutes) > 0 ? (
                              <button
                                type="button"
                                className={`efd-cell-btn${totalExpanded ? " efd-cell-btn--active" : ""}`}
                                onClick={() => toggleEftEntriesExpand(row.user_id, null)}
                              >
                                {fmtTime(Number(row.total_minutes) || 0)}
                              </button>
                            ) : fmtTime(Number(row.total_minutes) || 0)}
                          </td>
                        </tr>
                        {EFD_CATEGORY_COLUMNS.map((c) => {
                          const key = `${row.user_id}:${c.entryCategory}`;
                          const exp = eftEntriesExpanded[key];
                          if (!exp) return null;
                          return (
                            <tr key={key} className="efd-expand-row">
                              <td className="rv-td rv-td--sticky" />
                              <td className="rv-td" colSpan={EFD_CATEGORY_COLUMNS.length + 2}>
                                {exp.loading ? "Loading…" : (
                                  exp.rows.length === 0 ? "No entries found." : (
                                    <table className="efd-detail-table">
                                      <thead>
                                        <tr><th>Date</th><th className="efd-detail-table__type">Type</th><th>Reference</th><th>Hours</th></tr>
                                      </thead>
                                      <tbody>
                                        {exp.rows.map((r) => (
                                          <tr key={r.entry_id}>
                                            <td>{r.report_date}</td>
                                            <td className="efd-detail-table__type">{renderEfdType(r)}</td>
                                            <td>{renderEfdReference(r)}</td>
                                            <td>{fmtTime((Number(r.hours_spent) || 0) * 60 + (Number(r.minutes_spent) || 0))}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {totalExpanded && (
                          <tr className="efd-expand-row">
                            <td className="rv-td rv-td--sticky" />
                            <td className="rv-td" colSpan={EFD_CATEGORY_COLUMNS.length + 2}>
                              {totalExpanded.loading ? "Loading…" : (
                                totalExpanded.rows.length === 0 ? "No entries found." : (
                                  <table className="efd-detail-table">
                                    <thead>
                                      <tr><th>Date</th><th className="efd-detail-table__cat">Category</th><th className="efd-detail-table__type">Type</th><th>Reference</th><th>Hours</th></tr>
                                    </thead>
                                    <tbody>
                                      {totalExpanded.rows.map((r) => (
                                        <tr key={r.entry_id}>
                                          <td>{r.report_date}</td>
                                          <td className="efd-detail-table__cat">{r.category_name}</td>
                                          <td className="efd-detail-table__type">{renderEfdType(r)}</td>
                                          <td>{renderEfdReference(r)}</td>
                                          <td>{fmtTime((Number(r.hours_spent) || 0) * 60 + (Number(r.minutes_spent) || 0))}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {!eftEntriesLoading && eftEntriesData.rows.length > 0 && (
                    <tr className="rv-row">
                      <td className="rv-td rv-td--sticky" />
                      <td className="rv-td rv-td--sticky-user" style={{ fontWeight: 700 }}>Total</td>
                      {EFD_CATEGORY_COLUMNS.map((c) => (
                        <td key={`total-${c.key}`} className="rv-td rv-td--num" style={{ fontWeight: 700 }}>
                          {fmtTime(Number(eftEntriesData.totals?.[c.key]) || 0)}
                        </td>
                      ))}
                      <td className="rv-td rv-td--num" style={{ fontWeight: 700 }}>
                        {fmtTime(Number(eftEntriesData.totals?.total_minutes) || 0)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </div>
      )}

      {selectedReport === "efforts-product" && (
        <div className="rv-panel rv-panel--efp rv-efp-scope">
          <header className="page-head">
            <h1>Efforts Per Product</h1>
            <div className="page-head-actions">
              <button
                className="rv-download-btn"
                type="button"
                onClick={downloadProductEffortsExcel}
                disabled={loading || effortProductData.rows.length === 0}
              >
                <IconDownload />
                Export
              </button>
              <button className="mv2-new-task-btn v2-btn-primary" type="button" onClick={() => setSelectedReport(null)}>
                ← Back
              </button>
            </div>
          </header>
          <div className="page-toolbar">
            <div className="page-toolbar-right">
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">From</label>
                <input
                  type="date"
                  className="rv-toolbar__date"
                  value={dateFrom}
                  max={dateTo}
                  onChange={e => setDateFrom(e.target.value)}
                />
              </div>
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">To</label>
                <input
                  type="date"
                  className="rv-toolbar__date"
                  value={dateTo}
                  min={dateFrom}
                  onChange={e => setDateTo(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="rv-table-wrap rv-detail-wrap">
            <div className="rv-detail-scroll">
              <table className="rv-table rv-detail-table">
                <thead>
                  <tr>
                    <th className="rv-th rv-th--num rv-th--sticky">S.No</th>
                    <th className="rv-th rv-th--sticky-user">Product</th>
                    {effortProductData.categories.map((category) => (
                      <th key={category} className="rv-th rv-th--num">{category}</th>
                    ))}
                    <th className="rv-th rv-th--num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={effortProductData.categories.length + 3} className="rv-state">Loading…</td></tr>
                  )}
                  {!loading && effortProductData.rows.length === 0 && (
                    <tr><td colSpan={effortProductData.categories.length + 3} className="rv-state">No entries found for {dateLabel}</td></tr>
                  )}
                  {!loading && effortProductData.rows.map((row, i) => (
                    <tr key={row.product_name || `product-${i}`} className="rv-row">
                      <td className="rv-td rv-td--num rv-td--sticky">{i + 1}</td>
                      <td className="rv-td rv-td--sticky-user">{row.product_name || "Non-mapped"}</td>
                      {effortProductData.categories.map((category) => (
                        <td key={`${row.product_name}-${category}`} className="rv-td rv-td--num">
                          {fmtTime(Number(row.category_minutes?.[category]) || 0)}
                        </td>
                      ))}
                      <td className="rv-td rv-td--num" style={{ fontWeight: 700 }}>
                        {fmtTime(Number(row.total_minutes) || 0)}
                      </td>
                    </tr>
                  ))}
                  {!loading && effortProductData.rows.length > 0 && (
                    <tr className="rv-row">
                      <td className="rv-td rv-td--sticky" />
                      <td className="rv-td rv-td--sticky-user" style={{ fontWeight: 700 }}>Total</td>
                      {effortProductData.categories.map((category) => (
                        <td key={`product-total-${category}`} className="rv-td rv-td--num" style={{ fontWeight: 700 }}>
                          {fmtTime(Number(effortProductData.category_totals?.[category]) || 0)}
                        </td>
                      ))}
                      <td className="rv-td rv-td--num" style={{ fontWeight: 700 }}>
                        {fmtTime(Number(effortProductData.grand_total_minutes) || 0)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {selectedReport === "timesheet" && (
        <>
      <div className="page-head">
        <h1>Timesheet Entry <em>{inScopeRawList.length}</em></h1>
        <div className="page-head-actions">
          <button
            className="mv2-new-task-btn v2-btn-primary"
            type="button"
            style={{ "--mv2-accent": "#C8842E", "--mv2-radius": "8px" }}
            onClick={() => setSelectedReport(null)}
          >← Back</button>
        </div>
      </div>
      <div className="page-toolbar">
        <div className="page-toolbar-right">
          <FilterTriggerButton
            activeCount={tsFilterActiveCount}
            onClick={() => setTsFilterOpen(true)}
            onClear={() => { setDateFrom(""); setDateTo(""); setShowExcluded(false); }}
          />
        </div>
      </div>
      <ReportsDateFilterPopover
        open={tsFilterOpen}
        dateFrom={dateFrom}
        dateTo={dateTo}
        showExcluded={showExcluded}
        onApply={(from, to, showExcl) => { setDateFrom(from); setDateTo(to); setShowExcluded(showExcl); }}
        onClose={() => setTsFilterOpen(false)}
      />

      {/* Summary cards */}
      <div className="status-overview">
        <div className="status-overview__title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Timesheet Summary — {dateLabel}</span>
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: "inherit", letterSpacing: "inherit", color: "inherit", padding: 0 }}
          >
            {expanded ? "▲ Hide breakdown" : "▼ Show breakdown"}
          </button>
        </div>
        <div className="status-overview__grid">
          <div className="status-overview__item">
            <div className="status-overview__badge">Total Time Logged</div>
            <div className="status-overview__count">{loading ? "…" : fmtTime(summary.total_mins ?? 0)}</div>
            {!loading && (summary.avg_mins_per_user_day ?? 0) > 0 && (
              <div className="rv-so-avg">~{fmtTime(summary.avg_mins_per_user_day)} avg / user / day</div>
            )}
          </div>
          <div className="status-overview__item">
            <div className="status-overview__badge">Tasks Created / Completed</div>
            <div className="status-overview__count">
              {loading ? "…" : `${summary.tasks_count ?? "—"} / ${summary.tasks_completed_count ?? "—"}`}
            </div>
          </div>
          <div className="status-overview__item">
            <div className="status-overview__badge">Defects Created / Closed</div>
            <div className="status-overview__count">
              {loading ? "…" : `${summary.defects_count ?? "—"} / ${summary.defects_closed_count ?? "—"}`}
            </div>
          </div>
          <div className="status-overview__item">
            <div className="status-overview__badge">Meetings</div>
            <div className="status-overview__count">{loading ? "…" : `${summary.meetings_count ?? "—"}`}</div>
          </div>
        </div>
      </div>

      {/* Breakdown table */}
      {expanded && (
        <div className="rv-table-header">
          <span className="rv-table-title">Breakdown by User</span>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              className="rv-download-btn rv-download-btn--screen"
              onClick={() => setShowDetailView(v => !v)}
              disabled={loading || displayRows.length === 0}
              title="Show detailed breakdown on screen"
            >
              {showDetailView ? "▲ Hide Details" : "▼ Show Details"}
            </button>
            <button
              className="rv-download-btn"
              onClick={showDetailView ? downloadDetails : downloadExcel}
              disabled={loading || displayRows.length === 0}
              title="Download as Excel"
            >
              ↓ Download Excel
            </button>
          </div>
        </div>
      )}
      {expanded && !showDetailView && (
        <div className="page-table-wrap">
          <table className="page-table">
            <thead>
              <tr>
                <th className="rv-th rv-th--num">S.No</th>
                <th className="rv-th">User</th>
                <th className="rv-th rv-th--num">Tasks</th>
                <th className="rv-th rv-th--num">Defects</th>
                <th className="rv-th rv-th--num">Meetings</th>
                <th className="rv-th rv-th--num">Timesheet</th>
                <th className="rv-th rv-th--num">Avg. Hrs</th>
                <th className="rv-th rv-th--num">Hrs in Off</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="rv-state">Loading…</td></tr>
              )}
              {!loading && displayRows.length === 0 && (
                <tr><td colSpan={8} className="rv-state">No entries found for {dateLabel}</td></tr>
              )}
              {!loading && displayRows.map((row, i) => {
                const userTaskEntries   = rawList.filter(r => r.user_id === row.user_id && r.ref_type === "task");
                const userDefectEntries = rawList.filter(r => r.user_id === row.user_id && r.ref_type === "defect");
                return (
                  <tr key={row.user_id} className={`rv-row${row.is_excluded ? " rv-row--excluded" : ""}`}>
                    <td className="rv-td rv-td--num">{i + 1}</td>
                    <td className="rv-td">
                      {row.display_name}
                      {row.is_excluded && <span className="rv-excl-badge">EXCLUDED</span>}
                    </td>
                    <td className="rv-td rv-td--num">
                      {(row.task_ref_count || 0) > 0 ? (
                        <button className="rv-ref-link" onClick={() => setRefPopup({ title: `${row.display_name} — Task Entries`, entries: userTaskEntries })} type="button">
                          {row.task_ref_count}
                        </button>
                      ) : "—"}
                    </td>
                    <td className="rv-td rv-td--num">
                      {(row.defect_ref_count || 0) > 0 ? (
                        <button className="rv-ref-link" onClick={() => setRefPopup({ title: `${row.display_name} — Defect Entries`, entries: userDefectEntries })} type="button">
                          {row.defect_ref_count}
                        </button>
                      ) : "—"}
                    </td>
                    <td className="rv-td rv-td--num">
                      {(row.meeting_count || 0) > 0 ? (
                        <button
                          className="rv-ref-link"
                          onClick={() => setMeetingPopup({ title: `${row.display_name} — Meetings (${fmtTime(row.meeting_mins || 0)})`, entries: row.meeting_entries || [] })}
                          type="button"
                          title={`Total meeting time: ${fmtTime(row.meeting_mins || 0)}`}
                        >
                          {row.meeting_count}
                        </button>
                      ) : "—"}
                    </td>
                    <td className="rv-td rv-td--num">{fmtTime(row.total_mins || 0)}</td>
                    <td className="rv-td rv-td--num" style={(() => { const bg = timesheetBg(row.avg_mins_per_day || 0); return bg ? { background: bg, fontWeight: 600 } : {}; })()}>{fmtTime(row.avg_mins_per_day || 0)}</td>
                    <td className="rv-td rv-td--num" style={officeMismatchStyle(row.office_mins, row.total_mins)}>{fmtTime(row.office_mins || 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Detail pivot table */}
      {expanded && showDetailView && (() => {
        const dates    = getDatesInRange();
        const detailRows = buildDetailRows(displayRows);
        const totalCols  = 2 + dates.length * 4;
        return (
          <div className="page-table-wrap rv-detail-wrap">
            <div className="rv-detail-scroll">
              <table className="rv-detail-table page-table">
                <thead>
                  <tr>
                    <th className="rv-th rv-th--num rv-th--sticky" rowSpan={2}>S.No</th>
                    <th className="rv-th rv-th--sticky-user" rowSpan={2}>User</th>
                    {dates.map(d => (
                      <th key={d} className="rv-th rv-th--date-group" colSpan={4}>{fmtDateHeader(d)}</th>
                    ))}
                  </tr>
                  <tr>
                    {dates.map(d => (
                      <>
                        <th key={d + "-t"} className="rv-th rv-th--num rv-th--sub rv-th--icon" title="Tasks">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                        </th>
                        <th key={d + "-d"} className="rv-th rv-th--num rv-th--sub rv-th--icon" title="Defects">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2h8l1 4H7L8 2z"/><path d="M7 6c0 5 2 8 5 10 3-2 5-5 5-10"/><line x1="12" y1="6" x2="12" y2="16"/><line x1="7" y1="10" x2="17" y2="10"/></svg>
                        </th>
                        <th key={d + "-s"} className="rv-th rv-th--num rv-th--sub rv-th--icon" title="Timesheet">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        </th>
                        <th key={d + "-o"} className="rv-th rv-th--num rv-th--sub" title="Hrs in Off">Off</th>
                      </>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detailRows.length === 0 && (
                    <tr><td colSpan={totalCols} className="rv-state">No data</td></tr>
                  )}
                  {detailRows.map((u, i) => (
                    <tr key={u.display_name} className="rv-row">
                      <td className="rv-td rv-td--num rv-td--sticky">{i + 1}</td>
                      <td className="rv-td rv-td--sticky-user">{u.display_name}</td>
                      {dates.map(d => {
                        const dd   = u.byDate[d];
                        const mins = dd ? dd.totalMins : 0;
                        const officeMins = dd ? (Number(dd.officeMins) || 0) : 0;
                        const bg   = timesheetBg(mins);
                        const taskCount   = dd ? dd.taskIds.size   : 0;
                        const defectCount = dd ? dd.defectIds.size  : 0;
                        return (
                          <>
                            <td key={d + "-t"} className="rv-td rv-td--num">
                              {taskCount > 0 ? (
                                <button
                                  className="rv-ref-link"
                                  onClick={() => setRefPopup({
                                    title: `${u.display_name} — Task Entries (${fmtDisplayDate(d)})`,
                                    entries: rawList.filter(r => (r.user_id || r.user_name) === u.user_id && r.report_date?.slice(0, 10) === d && r.ref_type === "task"),
                                  })}
                                  type="button"
                                >{taskCount}</button>
                              ) : 0}
                            </td>
                            <td key={d + "-d"} className="rv-td rv-td--num">
                              {defectCount > 0 ? (
                                <button
                                  className="rv-ref-link"
                                  onClick={() => setRefPopup({
                                    title: `${u.display_name} — Defect Entries (${fmtDisplayDate(d)})`,
                                    entries: rawList.filter(r => (r.user_id || r.user_name) === u.user_id && r.report_date?.slice(0, 10) === d && r.ref_type === "defect"),
                                  })}
                                  type="button"
                                >{defectCount}</button>
                              ) : 0}
                            </td>
                            <td key={d + "-s"} className="rv-td rv-td--num" style={bg ? { background: bg, fontWeight: 600 } : {}}>
                              {fmtTime(mins)}
                            </td>
                            <td key={d + "-o"} className="rv-td rv-td--num" style={officeMismatchStyle(officeMins, mins)}>
                              {fmtTime(officeMins)}
                            </td>
                          </>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {refPopup && (
        <RefEntriesPopup
          title={refPopup.title}
          entries={refPopup.entries}
          onClose={() => setRefPopup(null)}
        />
      )}
      {meetingPopup && (
        <MeetingEntriesPopup
          title={meetingPopup.title}
          entries={meetingPopup.entries}
          onClose={() => setMeetingPopup(null)}
        />
      )}
        </>
      )}

      {selectedReport === "compliance-records" && (
        <div className="rv-panel">
          {/* Header + toolbar */}
          <div className="rv-panel__header rv-panel__header--with-toolbar">
            <div className="rv-panel__titlebar">
              <button className="rv-back-btn" type="button" onClick={() => setSelectedReport(null)}>← Back</button>
              <span className="rv-panel__title">Compliance Records</span>
            </div>
            <div className="rv-toolbar rv-toolbar--inline">
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">From</label>
                <input type="date" className="rv-toolbar__date" value={crDateFrom} max={crDateTo} onChange={e => setCrDateFrom(e.target.value)} />
              </div>
              <div className="rv-toolbar__group">
                <label className="rv-toolbar__label">To</label>
                <input type="date" className="rv-toolbar__date" value={crDateTo} min={crDateFrom} onChange={e => setCrDateTo(e.target.value)} />
              </div>
              <button className="rv-download-btn" type="button" onClick={fetchCrSummary} disabled={crLoading}>
                {crLoading ? "Loading…" : "Apply"}
              </button>
              <button className="rv-download-btn" type="button" onClick={downloadCrCsv} disabled={crLoading || crSorted.length === 0}>
                ↓ CSV
              </button>
            </div>
          </div>

          {/* Summary stat cards */}
          {!crLoading && crSummary.length > 0 && (
            <div className="cr-stat-row">
              {CR_TYPES.map(t => {
                const s = crOverallStats[t];
                const cls = s.rate >= 90 ? "cr-stat--green" : s.rate >= 70 ? "cr-stat--amber" : "cr-stat--red";
                return (
                  <div key={t} className={`cr-stat ${cls}`}>
                    <div className="cr-stat__label">{CR_TYPE_LABELS[t]}</div>
                    <div className="cr-stat__value">{s.rate}%</div>
                    <div className="cr-stat__sub">{s.compliant} / {s.total} days compliant</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Main grid */}
          <div className="rv-table-wrap">
            <table className="rv-table cr-grid">
              <thead>
                <tr>
                  <th className="rv-th rv-th--num">#</th>
                  <th className="rv-th cr-th--user" onClick={() => toggleCrSort("display_name")} style={{ cursor: "pointer" }}>
                    User {crSortKey === "display_name" ? (crSortDir === "asc" ? "↑" : "↓") : ""}
                  </th>
                  {CR_TYPES.map(t => (
                    <th key={t} className="rv-th rv-th--num" onClick={() => toggleCrSort(t)} style={{ cursor: "pointer" }}>
                      {CR_TYPE_LABELS[t]} {crSortKey === t ? (crSortDir === "asc" ? "↑" : "↓") : ""}
                    </th>
                  ))}
                  <th className="rv-th rv-th--num" onClick={() => toggleCrSort("overall")} style={{ cursor: "pointer" }}>
                    Overall {crSortKey === "overall" ? (crSortDir === "asc" ? "↑" : "↓") : ""}
                  </th>
                </tr>
              </thead>
              <tbody>
                {crLoading && <tr><td colSpan={7} className="rv-state">Loading…</td></tr>}
                {!crLoading && crSorted.length === 0 && (
                  <tr><td colSpan={7} className="rv-state">No compliance data found for this date range.</td></tr>
                )}
                {!crLoading && crSorted.map((u, i) => (
                  <tr key={u.user_id} className="rv-row">
                    <td className="rv-td rv-td--num">{i + 1}</td>
                    <td className="rv-td cr-td--user">{u.display_name || u.user_name}</td>
                    {CR_TYPES.map(t => {
                      const s = u[t];
                      const hasData = (s?.total_days || 0) > 0;
                      return (
                        <td key={t} className="rv-td rv-td--num">
                          {hasData ? (
                            <button
                              className={`cr-cell ${crRateClass(s.rate, t)}`}
                              type="button"
                              onClick={() => openCrDrawer(u.user_id, u.display_name || u.user_name, t)}
                              title={`${s.compliant_days}/${s.total_days} days compliant — click to view detail`}
                            >
                              <span className="cr-cell__rate">{fmtRate(s.rate)}</span>
                              <span className="cr-cell__days">{s.compliant_days}/{s.total_days}</span>
                            </button>
                          ) : <span className="cr-cell cr-cell--empty">—</span>}
                        </td>
                      );
                    })}
                    <td className="rv-td rv-td--num">
                      <span className={`cr-overall ${(u.overall_rate || 0) >= 90 ? "cr-overall--green" : (u.overall_rate || 0) >= 70 ? "cr-overall--amber" : "cr-overall--red"}`}>
                        {fmtRate(u.overall_rate || 0)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Detail drawer */}
          {crDrawer && (
            <div className="cr-drawer-overlay" onClick={() => setCrDrawer(null)}>
              <div className="cr-drawer" onClick={e => e.stopPropagation()}>
                <div className="cr-drawer__header">
                  <div className="cr-drawer__title">
                    <span className="cr-drawer__name">{crDrawer.displayName}</span>
                    <span className="cr-drawer__type">{CR_TYPE_LABELS[crDrawer.type]}</span>
                  </div>
                  <div className="cr-drawer__meta">{fmtDisplayDate(crDateFrom)} – {fmtDisplayDate(crDateTo)}</div>
                  <button className="cr-drawer__close" type="button" onClick={() => setCrDrawer(null)}>✕</button>
                </div>
                <div className="cr-drawer__body">
                  {crDrawerLoading && <div className="rv-state">Loading…</div>}
                  {!crDrawerLoading && crDrawerRows.length === 0 && (
                    <div className="rv-state">No records found.</div>
                  )}
                  {!crDrawerLoading && crDrawerRows.length > 0 && (
                    <table className="cr-detail-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Expected</th>
                          <th>Actual</th>
                          <th>Missed by</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {crDrawerRows.map((row, idx) => {
                          const isOpen = crExpandedRow === idx;
                          const details = row.details || {};
                          const hasItems = (details.tasks?.length || 0) > 0 || (details.defects?.length || 0) > 0;
                          return (
                            <>
                              <tr key={row.id || idx} className={`cr-detail-row ${row.is_compliant ? "cr-detail-row--ok" : "cr-detail-row--fail"}`}>
                                <td>{fmtDisplayDateShort(row.snapshot_date)}</td>
                                <td>
                                  <span className={`cr-badge ${row.is_compliant ? "cr-badge--ok" : "cr-badge--fail"}`}>
                                    {row.is_compliant ? "Compliant" : "Non-compliant"}
                                  </span>
                                </td>
                                <td className="cr-detail-num">
                                  {crDrawer.type === "timesheet" ? fmtTime(Number(row.expected_value) || 0)
                                    : crDrawer.type === "allocation" ? fmtHoursDecimal(row.expected_value)
                                    : `${Number(row.expected_value) || 0}`}
                                </td>
                                <td className="cr-detail-num">
                                  {crDrawer.type === "timesheet" ? fmtTime(Number(row.actual_value) || 0)
                                    : crDrawer.type === "allocation" ? fmtHoursDecimal(row.actual_value)
                                    : `${Number(row.actual_value) || 0}`}
                                </td>
                                <td className="cr-detail-num" style={!row.is_compliant ? { color: "#b91c1c", fontWeight: 700 } : {}}>
                                  {!row.is_compliant
                                    ? (crDrawer.type === "timesheet" ? fmtTime(Number(row.missed_by) || 0)
                                      : crDrawer.type === "allocation" ? fmtHoursDecimal(row.missed_by)
                                      : `${Number(row.missed_by) || 0}`)
                                    : "—"}
                                </td>
                                <td>
                                  {hasItems ? (
                                    <button className="cr-expand-btn" type="button" onClick={() => setCrExpandedRow(isOpen ? null : idx)}>
                                      {isOpen ? "▲ Hide" : "▼ Show"}
                                    </button>
                                  ) : "—"}
                                </td>
                              </tr>
                              {isOpen && hasItems && (
                                <tr className="cr-detail-expand">
                                  <td colSpan={6}>
                                    {(details.tasks || []).length > 0 && (
                                      <div className="cr-items-block">
                                        <div className="cr-items-block__title">Overdue Tasks</div>
                                        {details.tasks.map((t, ti) => (
                                          <div key={ti} className="cr-item">
                                            <span className="cr-item__code">{t.description || "—"}</span>
                                            <span className="cr-item__meta">{t.customer} · {t.project}</span>
                                            <span className="cr-item__due">Due: {fmtDisplayDateShort(t.due_date)}</span>
                                            <span className="cr-item__overdue">{t.days_overdue}d overdue</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {(details.defects || []).length > 0 && (
                                      <div className="cr-items-block">
                                        <div className="cr-items-block__title">Overdue Defects</div>
                                        {details.defects.map((d, di) => (
                                          <div key={di} className="cr-item">
                                            <span className="cr-item__code">{d.defect_code || "—"}</span>
                                            <span className="cr-item__title-txt">{d.title}</span>
                                            <span className={`cr-item__sev cr-item__sev--${(d.severity || "").toLowerCase()}`}>{d.severity}</span>
                                            <span className="cr-item__due">Due: {fmtDisplayDateShort(d.due_date)}</span>
                                            <span className="cr-item__overdue">{d.days_overdue}d overdue</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedReport === "epic-effort" && (
        <div className="rv-panel rv-panel--ee rv-ee-scope">
          {/* ── Header ── */}
          <header className="page-head">
            <h1>Epic Effort <em>{eeVisibleRows.length > 0 ? `${eeVisibleRows.length} epics` : ""}</em></h1>
            <div className="page-head-actions">
              {eeScoped && <span className="rv-scope-badge" title="Showing only epics under projects you're allocated to">Your allocated projects</span>}
              <button
                className="rv-download-btn"
                type="button"
                onClick={downloadEpicEffortExcel}
                disabled={eeLoading || eeVisibleRows.length === 0}
              >
                <IconDownload />
                Export
              </button>
              <button className="mv2-new-task-btn v2-btn-primary" type="button" onClick={() => setSelectedReport(null)}>
                ← Back
              </button>
            </div>
          </header>

          {/* ── Toolbar ── */}
          <div className="page-toolbar">
            <div className="page-toolbar-right">
              <div style={{ position: "relative" }}>
                <FilterTriggerButton
                  activeCount={eeActiveFilterCount}
                  onClick={() => setEeFilterOpen(v => !v)}
                  onClear={() => { setEeFilters(eeDefaultFilters()); setEeFilterMode("all"); }}
                />
              </div>
            </div>
          </div>

          {/* ── Filter drawer — shared ChoiceFilterPopover component, same
              one Projects/Users/Epics/ProductReleases use (alphabetical +
              selected-pinned options, field search, saved templates/
              favorites via SavedFiltersPanel, generic date-range presets).
              Filtering itself is now client-side over the once-fetched
              eeRows (see fetchEpicEffort/eeVisibleRows) — same architecture
              Tasks/Defects/Timesheet's own advanced filters already use. */}
          <ChoiceFilterPopover
            open={eeFilterOpen}
            title="Filter"
            fields={eeChoiceFields}
            appliedFilters={eeFilters}
            filterMode={eeFilterMode}
            onApply={(draft, mode) => { setEeFilters(draft); setEeFilterMode(mode || "all"); }}
            onClose={() => setEeFilterOpen(false)}
            module="epic_effort"
          />

          {/* ── Rows ── */}
          <div className="mv2-scroll-body">
            <div className="ee-list">
              {eeLoading && <div className="ee-list__state">Loading…</div>}
              {!eeLoading && eeVisibleRows.length === 0 && (
                <div className="ee-list__state">No epics match the current filters.</div>
              )}
              {!eeLoading && eeVisibleRows.map((row) => {
                const teamHours = row.team_hours || {};
                const teamSegs = eeTeams
                  .map((t, idx) => ({
                    name: t,
                    ...(teamHours[t] || { actual_hours: 0, pct: 0 }),
                    color: EFC_DONUT_COLORS[idx % EFC_DONUT_COLORS.length],
                  }))
                  .filter((seg) => seg.actual_hours > 0);
                const paceStatus = row.pace_status || "on_pace";
                return (
                  <div className="ee-row" key={row.epic_id}>
                    <div
                      className="ee-row__pace"
                      style={{ background: EE_PACE_COLORS[paceStatus] || EE_PACE_COLORS.on_pace }}
                      title={EE_PACE_LABELS[paceStatus] || "On pace"}
                    />
                    <div className="ee-row__code">{row.epic_code || "—"}</div>
                    <div className="ee-row__info">
                      <div className="ee-row__name">{row.epic_name || "—"}</div>
                      <div className="ee-row__meta">
                        {row.customer_name || "—"} -{" "}
                        <span className={`ee-status-badge ee-status-badge--${(row.epic_status || "").toLowerCase().replace(/[^a-z]/g, "-")}`}>
                          {row.epic_status || "—"}
                        </span>{" "}
                        - {row.am_name || "—"}
                      </div>
                      <div className="ee-row__dates">
                        {row.start_date ? fmtDisplayDate(row.start_date) : "—"} – {row.end_date ? fmtDisplayDate(row.end_date) : "—"}
                      </div>
                    </div>
                    <div className="ee-row__effort" title={EE_PACE_LABELS[paceStatus] || "On pace"}>
                      <span
                        className="ee-row__actual"
                        style={{ color: EE_PACE_COLORS[paceStatus] || EE_PACE_COLORS.on_pace }}
                      >
                        {fmtHoursDecimal(row.actual_hours)}
                      </span>
                      <span className="ee-row__sep">/</span>
                      <span className="ee-row__est">{fmtHoursDecimal(row.estimated_hours)}</span>
                    </div>
                    <div className="ee-row__teamcol">
                      <div className="ee-row__teambar" title={teamSegs.length === 0 ? "No team-mapped effort logged" : undefined}>
                        {teamSegs.length > 0 ? (
                          teamSegs.map((seg) => (
                            <div
                              key={seg.name}
                              className="ee-row__teamseg"
                              style={{ flex: `${seg.pct} 1 0`, background: seg.color }}
                              onMouseEnter={(e) => showEeTeamTip(e.currentTarget, seg)}
                              onMouseLeave={hideEeTeamTip}
                            >
                              {seg.pct >= 8 && <span className="ee-row__teamseg-pct">{seg.pct}%</span>}
                            </div>
                          ))
                        ) : (
                          <div className="ee-row__teamseg ee-row__teamseg--empty" />
                        )}
                      </div>
                      {teamSegs.length > 0 && (
                        <div className="ee-row__teamlabels">
                          {teamSegs.map((seg) => (
                            <div
                              key={seg.name}
                              className="ee-row__teamlabel"
                              style={{ flex: `${seg.pct} 1 0` }}
                              title={seg.name}
                            >
                              {seg.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div ref={eeTeamTipRef} className="ee-teamseg-tip" />

          {/* ── Footer ── */}
          <div className="mv2-table-footer">
            <span className="mv2-footer-info">
              {eeVisibleRows.length} epic{eeVisibleRows.length !== 1 ? "s" : ""}
              {eeVisibleRows.length > 0 && (() => {
                const total = eeVisibleRows.reduce((s, r) => s + (Number(r.actual_hours) || 0), 0);
                return total > 0 ? ` · ${fmtHoursDecimal(total)} actual` : "";
              })()}
            </span>
          </div>
        </div>
      )}

      <TaskQuickViewDrawer taskId={quickView?.type === "task" ? quickView.id : null} onClose={closeQuickView} />
      <DefectQuickViewDrawer defectId={quickView?.type === "defect" ? quickView.id : null} onClose={closeQuickView} />
    </div>
  );
}
